import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

export default function Farmacia() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState('dispensar');

  return (
    <div style={{ padding: '24px 28px' }}>
      <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--navy)', marginBottom: 4 }}>Farmacia</h1>
      <p style={{ fontSize: 12.5, color: 'var(--text3)', marginBottom: 20 }}>
        Dispensación de recetas y validación de precios tras cada compra.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
        {[['resumen', '🏠 Resumen'], ['dispensar', '💊 Dispensación'], ['precios', '💰 Precios pendientes'], ['proveedores', '🏢 Proveedores'], ['catalogo', '🗂️ Catálogo'], ['pedidos', '📝 Pedidos de compra'], ['compras', '📦 Ingresos de compra'], ['regulaciones', '⚖️ Regulaciones'], ['reportes', '📊 Kardex y reportes']].map(([t, l]) => (
          <div key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '10px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              color: tab === t ? 'var(--navy)' : 'var(--text3)',
              borderBottom: tab === t ? '2px solid var(--blue-mid)' : '2px solid transparent',
            }}>
            {l}
          </div>
        ))}
      </div>

      {tab === 'resumen' && <PanelResumen />}
      {tab === 'dispensar' && <PanelDispensacion codRecetaInicial={params.get('receta')} codPacienteInicial={params.get('paciente')} />}
      {tab === 'precios' && <PanelPrecios />}
      {tab === 'proveedores' && <PanelProveedores />}
      {tab === 'catalogo' && <PanelCatalogo />}
      {tab === 'pedidos' && <PanelPedidos />}
      {tab === 'compras' && <PanelCompras />}
      {tab === 'regulaciones' && <PanelRegulaciones />}
      {tab === 'reportes' && <PanelReportesKardex />}
    </div>
  );
}

/* ── Panel de Resumen (KPIs + movimientos 7 días) ── */
function PanelResumen() {
  const [cargando, setCargando] = useState(true);
  const [cola, setCola] = useState([]);
  const [meds, setMeds] = useState([]);
  const [pedidos, setPedidos] = useState([]);
  const [movs, setMovs] = useState([]);

  useEffect(() => {
    (async () => {
      setCargando(true);
      try {
        const desde = new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10);
        const [rCola, rMeds, rPedidos, rMovs] = await Promise.all([
          api.get('/farmacia/recetas-pendientes'),
          api.get('/farmacia/medicamentos?activo=1&limit=500'),
          api.get('/farmacia/pedidos'),
          api.get(`/farmacia/reportes/movimientos?desde=${desde}`),
        ]);
        setCola(rCola.data);
        setMeds(rMeds.data);
        setPedidos(rPedidos.data);
        setMovs(rMovs.data);
      } catch (e) { /* KPIs quedan en 0 si algo falla */ }
      setCargando(false);
    })();
  }, []);

  if (cargando) return <div className="spinner" />;

  const urgentes = cola.filter((r) => (Date.now() - new Date(r.FECHA).getTime()) / 60000 > 90).length;
  const stockBajo = meds.filter((m) => parseFloat(m.STOCK_MINIMO) > 0 && parseFloat(m.STOCK) < parseFloat(m.STOCK_MINIMO));
  const pedidosTransito = pedidos.filter((p) => p.C_ESTADO === 'PEN' || p.C_ESTADO === 'PAR').length;
  const valorInventario = meds.reduce((acc, m) => acc + (parseFloat(m.STOCK) || 0) * (parseFloat(m.COSTO_PROMEDIO) || 0), 0);

  const dias = [...Array(7)].map((_, i) => { const d = new Date(); d.setDate(d.getDate() - (6 - i)); return d; });
  const porDia = dias.map((d) => {
    const key = d.toISOString().slice(0, 10);
    const delDia = movs.filter((m) => String(m.FECHA).slice(0, 10) === key);
    const entradas = delDia.filter((m) => ['COM', 'AJP', 'ING'].includes(m.TIPO)).reduce((a, m) => a + Math.abs(parseFloat(m.CANTIDAD) || 0), 0);
    const salidas = delDia.filter((m) => ['VEN', 'AJN'].includes(m.TIPO)).reduce((a, m) => a + Math.abs(parseFloat(m.CANTIDAD) || 0), 0);
    return { dia: d.toLocaleDateString('es-EC', { weekday: 'short' }), entradas, salidas };
  });
  const maxVal = Math.max(1, ...porDia.flatMap((d) => [d.entradas, d.salidas]));

  return (
    <div>
      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-icon" style={{ background: 'var(--red-lt)', color: 'var(--red)' }}>📋</div>
          <div>
            <div className="metric-value">{cola.length}</div>
            <div className="metric-label">Recetas pendientes{urgentes > 0 ? ` · ${urgentes} urgentes` : ''}</div>
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-icon" style={{ background: 'var(--amber-lt)', color: 'var(--amber)' }}>⚠️</div>
          <div>
            <div className="metric-value">{stockBajo.length}</div>
            <div className="metric-label">Medicamentos bajo el mínimo</div>
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-icon" style={{ background: 'var(--blue-lt)', color: 'var(--blue-mid)' }}>🚚</div>
          <div>
            <div className="metric-value">{pedidosTransito}</div>
            <div className="metric-label">Pedidos en tránsito</div>
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-icon" style={{ background: 'var(--green-lt)', color: 'var(--green)' }}>💵</div>
          <div>
            <div className="metric-value">${valorInventario.toFixed(2)}</div>
            <div className="metric-label">Valor de inventario (costo prom.)</div>
          </div>
        </div>
      </div>

      {stockBajo.length > 0 && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 18px', marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)', marginBottom: 10 }}>Medicamentos bajo el mínimo</div>
          {stockBajo.slice(0, 8).map((m) => (
            <div key={m.COD} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--bg)', fontSize: 12.5 }}>
              <span style={{ color: 'var(--text)' }}>{m.MEDICAMENTO}</span>
              <span style={{ color: 'var(--amber)', fontWeight: 600 }}>{m.STOCK} / mín. {m.STOCK_MINIMO}</span>
            </div>
          ))}
          {stockBajo.length > 8 && <div style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 8 }}>+ {stockBajo.length - 8} más — ver Catálogo</div>}
        </div>
      )}

      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 18px' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)', marginBottom: 14 }}>Movimientos — últimos 7 días</div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 18, height: 150, padding: '0 6px' }}>
          {porDia.map((d, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 120 }}>
                <div style={{ width: 12, borderRadius: '3px 3px 0 0', background: 'var(--green)', height: Math.max(2, (d.entradas / maxVal) * 120) }} title={`Entradas: ${d.entradas}`} />
                <div style={{ width: 12, borderRadius: '3px 3px 0 0', background: 'var(--navy)', height: Math.max(2, (d.salidas / maxVal) * 120) }} title={`Salidas: ${d.salidas}`} />
              </div>
              <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 600, textTransform: 'capitalize' }}>{d.dia}</div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end', marginTop: 10, fontSize: 11, color: 'var(--text3)' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><i style={{ width: 9, height: 9, borderRadius: 2, background: 'var(--green)', display: 'inline-block' }} />Entradas</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><i style={{ width: 9, height: 9, borderRadius: 2, background: 'var(--navy)', display: 'inline-block' }} />Salidas</span>
        </div>
      </div>
    </div>
  );
}
/* ── Panel de Kardex y reportes de movimientos ── */
const TIPOS_MOV = { COM:{l:'Compra',bg:'var(--green-lt)',c:'var(--green)'}, EGR:{l:'Egreso/Venta',bg:'var(--red-lt)',c:'var(--red)'},
                     ING:{l:'Ingreso',bg:'var(--blue-lt)',c:'var(--blue-mid)'}, INC:{l:'Stock inicial',bg:'var(--surface2)',c:'var(--text3)'},
                     AJP:{l:'Ajuste (+)',bg:'var(--green-lt)',c:'var(--green)'}, AJN:{l:'Ajuste (-)',bg:'var(--red-lt)',c:'var(--red)'} };
function PanelReportesKardex() {
  const [vista, setVista] = useState('kardex'); // 'kardex' | 'movimientos'
  return (
    <div>
      <div style={{display:'flex',gap:8,marginBottom:16}}>
        {[['kardex','Kardex por medicamento'],['movimientos','Movimientos generales']].map(([v,l])=>(
          <div key={v} onClick={()=>setVista(v)} style={{padding:'6px 14px',fontSize:12,fontWeight:600,cursor:'pointer',
            borderRadius:16,background:vista===v?'var(--navy)':'var(--bg)',color:vista===v?'var(--surface)':'var(--text3)'}}>{l}</div>
        ))}
      </div>
      {vista==='kardex' ? <SubKardex /> : <SubMovimientos />}
    </div>
  );
}
function SubKardex() {
  const [busqMed, setBusqMed] = useState('');
  const [sugsMed, setSugsMed] = useState([]);
  const [medSel, setMedSel] = useState(null);
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    if (busqMed.length < 2) { setSugsMed([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/historia/medicamentos?q=${encodeURIComponent(busqMed)}`); setSugsMed(data||[]); } catch { setSugsMed([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [busqMed]);

  const elegir = async (m) => {
    setMedSel(m); setBusqMed(''); setSugsMed([]); setCargando(true);
    try { const { data } = await api.get(`/farmacia/kardex/${m.COD}`); setData(data); }
    catch (e) { alert(e.response?.data?.error || e.message); }
    setCargando(false);
  };

  return (
    <div>
      <div style={{marginBottom:14,position:'relative',maxWidth:400}}>
        <input className="form-control" style={{fontSize:12}} placeholder="Buscar medicamento para ver su kardex..."
          value={busqMed} onChange={e=>setBusqMed(e.target.value)} />
        {sugsMed.length>0 && (
          <div style={{position:'absolute',top:'100%',left:0,right:0,background:'var(--surface)',border:'1px solid var(--border)',
            borderRadius:5,zIndex:10,maxHeight:160,overflowY:'auto',boxShadow:'0 4px 12px rgba(0,0,0,.1)'}}>
            {sugsMed.map(m=>(
              <div key={m.COD} onClick={()=>elegir(m)} style={{padding:'6px 10px',fontSize:12,cursor:'pointer',borderBottom:'1px solid var(--surface2)'}}
                onMouseEnter={e=>e.currentTarget.style.background='var(--blue-lt)'}
                onMouseLeave={e=>e.currentTarget.style.background=''}>
                {m.MEDICAMENTO}
              </div>
            ))}
          </div>
        )}
      </div>
      {cargando && <div style={{fontSize:12,color:'var(--text3)'}}>Cargando...</div>}
      {data && (
        <>
          <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,padding:14,marginBottom:14,display:'flex',gap:24}}>
            <div><div style={{fontSize:10,color:'var(--text3)',textTransform:'uppercase'}}>Medicamento</div><div style={{fontSize:13,fontWeight:600}}>{data.medicamento.MEDICAMENTO}</div></div>
            <div><div style={{fontSize:10,color:'var(--text3)',textTransform:'uppercase'}}>Stock actual</div><div style={{fontSize:13,fontWeight:600}}>{data.medicamento.STOCK}</div></div>
            <div><div style={{fontSize:10,color:'var(--text3)',textTransform:'uppercase'}}>Costo promedio</div><div style={{fontSize:13,fontWeight:600}}>${parseFloat(data.medicamento.COSTO_PROMEDIO).toFixed(2)}</div></div>
          </div>
          <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,overflow:'hidden'}}>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead>
                <tr style={{background:'var(--blue-lt)'}}>
                  {['Fecha','Tipo','Cantidad','Valor unit.','Saldo','Proveedor/Usuario','Detalle'].map(h=>(
                    <th key={h} style={{padding:'8px 12px',fontSize:10,fontWeight:700,textTransform:'uppercase',
                      letterSpacing:'.4px',color:'var(--navy)',textAlign:'left',borderBottom:'1px solid var(--border)'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.movimientos.length===0 && (
                  <tr><td colSpan={7} style={{textAlign:'center',padding:20,color:'var(--text3)',fontSize:12}}>Sin movimientos registrados</td></tr>
                )}
                {data.movimientos.map(m=>{
                  const esEgreso = m.TIPO==='EGR'||m.TIPO==='AJN';
                  return (
                    <tr key={m.COD} style={{borderBottom:'1px solid var(--bg)'}}>
                      <td style={{padding:'8px 12px',fontSize:12}}>{m.FECHA?.slice(0,16).replace('T',' ')}</td>
                      <td style={{padding:'8px 12px',fontSize:11}}>
                        <span style={{display:'inline-flex',alignItems:'center',padding:'3px 8px',borderRadius:20,fontSize:10,fontWeight:600,
                          background:TIPOS_MOV[m.TIPO]?.bg||'var(--surface2)',color:TIPOS_MOV[m.TIPO]?.c||'var(--text3)'}}>
                          {TIPOS_MOV[m.TIPO]?.l||m.TIPO}
                        </span>
                      </td>
                      <td style={{padding:'8px 12px',fontSize:12,fontWeight:600,color:esEgreso?'var(--red)':'var(--green)'}}>
                        {esEgreso?'-':'+'}{m.CANTIDAD}
                      </td>
                      <td style={{padding:'8px 12px',fontSize:12}}>${parseFloat(m.VALOR_UNITARIO||0).toFixed(2)}</td>
                      <td style={{padding:'8px 12px',fontSize:12,fontWeight:600}}>{m.SALDO}</td>
                      <td style={{padding:'8px 12px',fontSize:12}}>{m.PROVEEDOR||m.USUARIO||'—'}</td>
                      <td style={{padding:'8px 12px',fontSize:12,color:'var(--text3)'}}>{m.LOTE?`Lote ${m.LOTE}`:''} {m.OBSERVACIONES||''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
const CATEGORIAS_MOV = [['','Todos'],['compras','Compras'],['ventas','Ventas'],['ajustes','Ajustes'],['anulados','Anulados']];
function badgeMov(m) {
  if (m.ESTADO==='ANL') return { l:'Anulado', bg:'var(--surface2)', c:'var(--text3)' };
  return TIPOS_MOV[m.TIPO] ? { l:(m.TIPO==='EGR'?'Venta':TIPOS_MOV[m.TIPO].l), bg:TIPOS_MOV[m.TIPO].bg, c:TIPOS_MOV[m.TIPO].c }
    : { l:m.TIPO, bg:'var(--surface2)', c:'var(--text3)' };
}
function SubMovimientos() {
  const PAGE_SIZE = 25;
  const [filtros, setFiltros] = useState({ desde:'', hasta:'', categoria:'', q:'', usuario:'' });
  const [pagina, setPagina] = useState(1);
  const [lista, setLista] = useState([]);
  const [total, setTotal] = useState(0);
  const [resumen, setResumen] = useState({});
  const [usuarios, setUsuarios] = useState([]);
  const [cargando, setCargando] = useState(true);

  useEffect(() => { api.get('/farmacia/reportes/usuarios').then(({data})=>setUsuarios(data||[])).catch(()=>{}); }, []);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const p = new URLSearchParams();
      if (filtros.desde) p.set('desde', filtros.desde);
      if (filtros.hasta) p.set('hasta', filtros.hasta);
      if (filtros.categoria) p.set('categoria', filtros.categoria);
      if (filtros.q) p.set('q', filtros.q);
      if (filtros.usuario) p.set('usuario', filtros.usuario);
      p.set('page', pagina); p.set('pageSize', PAGE_SIZE);
      const { data } = await api.get(`/farmacia/reportes/movimientos?${p.toString()}`);
      setLista(data.rows||[]); setTotal(data.total||0); setResumen(data.resumen||{});
    } catch (e) { console.error(e); }
    setCargando(false);
  }, [filtros, pagina]);
  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { setPagina(1); }, [filtros.desde, filtros.hasta, filtros.categoria, filtros.q, filtros.usuario]);

  const totalPaginas = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const iniFila = total===0 ? 0 : (pagina-1)*PAGE_SIZE+1;
  const finFila = Math.min(pagina*PAGE_SIZE, total);
  const numsPagina = () => {
    const start = Math.max(1, pagina-2), end = Math.min(totalPaginas, pagina+2);
    const nums = []; for (let p=start; p<=end; p++) nums.push(p); return nums;
  };

  const exportarExcel = () => {
    const headers = ['Fecha','Tipo','Medicamento','Cantidad','Stock resultante','Usuario'];
    const filas = lista.map(m => {
      const esEgreso = m.TIPO==='EGR'||m.TIPO==='AJN';
      return [m.FECHA?.slice(0,16).replace('T',' ')||'', badgeMov(m).l, m.MEDICAMENTO,
        `${esEgreso?'-':'+'}${m.CANTIDAD}`, m.SALDO, m.USUARIO||''];
    });
    const csv = [headers, ...filas].map(f=>f.map(v=>`"${String(v??'').replace(/"/g,'""')}"`).join(',')).join('\r\n');
    const blob = new Blob(['﻿'+csv], { type:'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `kardex_movimientos_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div style={{background:'var(--amber-lt)',border:'1px solid var(--amber)',borderRadius:12,padding:'12px 16px',marginBottom:16,fontSize:12.5,color:'var(--text)',lineHeight:1.5}}>
        <b>Qué cambia:</b> antes el Kardex se revisaba como una sola lista larga. Con filtros por fecha/medicamento/usuario/tipo y un resumen arriba, un movimiento puntual se encuentra en segundos.
      </div>

      <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,padding:16,marginBottom:16}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12,flexWrap:'wrap',gap:8}}>
          <div style={{fontSize:13,fontWeight:700,color:'var(--navy)'}}>Filtros</div>
          <button className="btn btn-sm" disabled={lista.length===0} onClick={exportarExcel}>Exportar a Excel ⬇</button>
        </div>
        <div style={{display:'flex',gap:10,marginBottom:12,alignItems:'flex-end',flexWrap:'wrap'}}>
          <div>
            <label className="form-label" style={{fontSize:10}}>Desde</label>
            <input type="date" className="form-control" style={{fontSize:12}} value={filtros.desde}
              onChange={e=>setFiltros(f=>({...f,desde:e.target.value}))} />
          </div>
          <div>
            <label className="form-label" style={{fontSize:10}}>Hasta</label>
            <input type="date" className="form-control" style={{fontSize:12}} value={filtros.hasta}
              onChange={e=>setFiltros(f=>({...f,hasta:e.target.value}))} />
          </div>
          <div style={{flex:1,minWidth:160}}>
            <label className="form-label" style={{fontSize:10}}>Medicamento</label>
            <input className="form-control" style={{fontSize:12}} placeholder="Buscar medicamento..."
              value={filtros.q} onChange={e=>setFiltros(f=>({...f,q:e.target.value}))} />
          </div>
          <div>
            <label className="form-label" style={{fontSize:10}}>Usuario</label>
            <select className="form-control" style={{fontSize:12}} value={filtros.usuario}
              onChange={e=>setFiltros(f=>({...f,usuario:e.target.value}))}>
              <option value="">Todos</option>
              {usuarios.map(u=><option key={u} value={u}>{u}</option>)}
            </select>
          </div>
        </div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          {CATEGORIAS_MOV.map(([v,l])=>(
            <div key={v} onClick={()=>setFiltros(f=>({...f,categoria:v}))} style={{padding:'6px 14px',fontSize:12,fontWeight:600,cursor:'pointer',
              borderRadius:16,border:'1px solid var(--border)',
              background:filtros.categoria===v?'var(--navy)':'var(--surface)',
              color:filtros.categoria===v?'var(--surface)':'var(--text3)'}}>{l}</div>
          ))}
        </div>
      </div>

      <div className="metrics-grid" style={{marginBottom:16}}>
        <div className="metric-card">
          <div className="metric-icon" style={{background:'var(--green-lt)',color:'var(--green)'}}>⬆️</div>
          <div><div className="metric-value">+{resumen.ENTRADAS||0} u.</div><div className="metric-label">Entradas</div></div>
        </div>
        <div className="metric-card">
          <div className="metric-icon" style={{background:'var(--blue-lt)',color:'var(--blue-mid)'}}>⬇️</div>
          <div><div className="metric-value">-{resumen.SALIDAS||0} u.</div><div className="metric-label">Salidas</div></div>
        </div>
        <div className="metric-card">
          <div className="metric-icon" style={{background:'var(--amber-lt)',color:'var(--amber)'}}>⚖️</div>
          <div><div className="metric-value">{parseFloat(resumen.AJUSTES||0)>0?'+':''}{resumen.AJUSTES||0} u.</div><div className="metric-label">Ajustes</div></div>
        </div>
        <div className="metric-card">
          <div className="metric-icon" style={{background:'var(--surface2)',color:'var(--navy)'}}>💵</div>
          <div><div className="metric-value">${parseFloat(resumen.VALOR_MOVIDO||0).toFixed(2)}</div><div className="metric-label">Valor movido</div></div>
        </div>
      </div>

      <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,overflow:'hidden'}}>
        <table style={{width:'100%',borderCollapse:'collapse'}}>
          <thead>
            <tr style={{background:'var(--blue-lt)'}}>
              {['Fecha','Tipo','Medicamento','Cant.','Stock result.','Usuario'].map(h=>(
                <th key={h} style={{padding:'8px 12px',fontSize:10,fontWeight:700,textTransform:'uppercase',
                  letterSpacing:'.4px',color:'var(--navy)',textAlign:'left',borderBottom:'1px solid var(--border)'}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cargando && <tr><td colSpan={6} style={{textAlign:'center',padding:20,color:'var(--text3)',fontSize:12}}>Cargando...</td></tr>}
            {!cargando && lista.length===0 && (
              <tr><td colSpan={6} style={{textAlign:'center',padding:20,color:'var(--text3)',fontSize:12}}>Sin movimientos en ese rango</td></tr>
            )}
            {!cargando && lista.map(m=>{
              const b = badgeMov(m);
              const esEgreso = m.TIPO==='EGR'||m.TIPO==='AJN';
              return (
                <tr key={m.COD} style={{borderBottom:'1px solid var(--bg)'}}>
                  <td style={{padding:'8px 12px',fontSize:12}}>{m.FECHA?.slice(0,16).replace('T',' ')}</td>
                  <td style={{padding:'8px 12px',fontSize:11}}>
                    <span style={{display:'inline-flex',alignItems:'center',padding:'3px 8px',borderRadius:20,fontSize:10,fontWeight:600,
                      background:b.bg,color:b.c}}>{b.l}</span>
                  </td>
                  <td style={{padding:'8px 12px',fontSize:12,fontWeight:600}}>{m.MEDICAMENTO}</td>
                  <td style={{padding:'8px 12px',fontSize:12,fontWeight:600,color:esEgreso?'var(--red)':'var(--green)'}}>
                    {esEgreso?'-':'+'}{m.CANTIDAD}
                  </td>
                  <td style={{padding:'8px 12px',fontSize:12,fontWeight:600}}>{m.SALDO}</td>
                  <td style={{padding:'8px 12px',fontSize:12}}>{m.USUARIO}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 16px',
          borderTop:'1px solid var(--border)',background:'var(--surface2)',fontSize:12,color:'var(--text3)',flexWrap:'wrap',gap:10}}>
          <span>{total===0 ? 'Sin resultados' : `Mostrando ${iniFila}–${finFila} de ${total.toLocaleString('es-EC')} movimientos en el rango`}</span>
          <div style={{display:'flex',gap:4,alignItems:'center'}}>
            <button className="btn btn-sm" disabled={pagina===1} onClick={()=>setPagina(1)}>«</button>
            <button className="btn btn-sm" disabled={pagina===1} onClick={()=>setPagina(p=>p-1)}>‹</button>
            {numsPagina().map(p=>(
              <button key={p} onClick={()=>setPagina(p)}
                className="btn btn-sm"
                style={p===pagina?{background:'var(--navy)',color:'var(--surface)',borderColor:'var(--navy)',fontWeight:700}:undefined}>
                {p}
              </button>
            ))}
            <button className="btn btn-sm" disabled={pagina===totalPaginas} onClick={()=>setPagina(p=>p+1)}>›</button>
            <button className="btn btn-sm" disabled={pagina===totalPaginas} onClick={()=>setPagina(totalPaginas)}>»</button>
          </div>
        </div>
      </div>
    </div>
  );
}
/* ── Panel de regulaciones (ajustes +/-) ── */
function PanelRegulaciones() {
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [busqMed, setBusqMed] = useState('');
  const [sugsMed, setSugsMed] = useState([]);
  const [medSel, setMedSel] = useState(null);
  const [tipo, setTipo] = useState('AJP');
  const [cantidad, setCantidad] = useState('');
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');

  const cargar = useCallback(async () => {
    setCargando(true);
    try { const { data } = await api.get('/farmacia/regulaciones'); setLista(data||[]); }
    catch (e) { console.error(e); }
    setCargando(false);
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    if (busqMed.length < 2) { setSugsMed([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/historia/medicamentos?q=${encodeURIComponent(busqMed)}`); setSugsMed(data||[]); } catch { setSugsMed([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [busqMed]);

  const registrar = async () => {
    if (!medSel) { setErr('Selecciona un medicamento'); return; }
    if (!cantidad || parseFloat(cantidad) <= 0) { setErr('Cantidad inválida'); return; }
    if (!motivo.trim()) { setErr('El motivo es obligatorio'); return; }
    setSaving(true); setErr(''); setOk('');
    try {
      await api.post('/farmacia/regulacion', { COD_MEDICAMENTO: medSel.COD, CANTIDAD: cantidad, TIPO: tipo, MOTIVO: motivo });
      setOk('✓ Ajuste registrado correctamente');
      setMedSel(null); setBusqMed(''); setCantidad(''); setMotivo('');
      cargar();
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    setSaving(false);
  };

  return (
    <div>
      <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,padding:16,marginBottom:20,maxWidth:560}}>
        <div style={{fontSize:13,fontWeight:600,marginBottom:12}}>Registrar ajuste de inventario</div>
        <div style={{marginBottom:10,position:'relative'}}>
          <label className="form-label" style={{fontSize:10}}>Medicamento</label>
          {medSel ? (
            <div style={{display:'flex',alignItems:'center',gap:6,background:'var(--blue-lt)',borderRadius:5,padding:'6px 10px',fontSize:12}}>
              <span style={{flex:1,fontWeight:600}}>{medSel.MEDICAMENTO}</span>
              <span style={{cursor:'pointer'}} onClick={()=>setMedSel(null)}>&#x2715;</span>
            </div>
          ) : (
            <>
              <input className="form-control" style={{fontSize:12}} placeholder="Buscar medicamento..."
                value={busqMed} onChange={e=>setBusqMed(e.target.value)} />
              {sugsMed.length>0 && (
                <div style={{position:'absolute',top:'100%',left:0,right:0,background:'var(--surface)',border:'1px solid var(--border)',
                  borderRadius:5,zIndex:10,maxHeight:160,overflowY:'auto',boxShadow:'0 4px 12px rgba(0,0,0,.1)'}}>
                  {sugsMed.map(m=>(
                    <div key={m.COD} onClick={()=>{setMedSel(m);setBusqMed('');setSugsMed([]);}} style={{padding:'6px 10px',fontSize:12,cursor:'pointer',borderBottom:'1px solid var(--surface2)'}}
                      onMouseEnter={e=>e.currentTarget.style.background='var(--blue-lt)'}
                      onMouseLeave={e=>e.currentTarget.style.background=''}>
                      {m.MEDICAMENTO}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
          <div>
            <label className="form-label" style={{fontSize:10}}>Tipo de ajuste</label>
            <select className="form-control" style={{fontSize:12}} value={tipo} onChange={e=>setTipo(e.target.value)}>
              <option value="AJP">➕ Positivo (aumenta stock)</option>
              <option value="AJN">➖ Negativo (disminuye stock)</option>
            </select>
          </div>
          <div>
            <label className="form-label" style={{fontSize:10}}>Cantidad</label>
            <input type="number" min="0" step="0.01" className="form-control" style={{fontSize:12}}
              value={cantidad} onChange={e=>setCantidad(e.target.value)} />
          </div>
        </div>
        <div style={{marginBottom:10}}>
          <label className="form-label" style={{fontSize:10}}>Motivo *</label>
          <textarea className="form-control" style={{fontSize:12,resize:'vertical'}} rows={2}
            placeholder="Ej: merma por vencimiento, conteo físico, producto dañado..."
            value={motivo} onChange={e=>setMotivo(e.target.value)} />
        </div>
        {err && <div style={{color:'var(--red)',fontSize:11,marginBottom:10}}>{err}</div>}
        {ok && <div style={{color:'var(--green)',fontSize:11,marginBottom:10}}>{ok}</div>}
        <button className="btn btn-primary btn-sm" disabled={saving} onClick={registrar}>
          {saving ? 'Guardando...' : 'Registrar ajuste'}
        </button>
      </div>

      <div style={{fontSize:13,fontWeight:600,marginBottom:10}}>Historial de ajustes</div>
      <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,overflow:'hidden'}}>
        <table style={{width:'100%',borderCollapse:'collapse'}}>
          <thead>
            <tr style={{background:'var(--blue-lt)'}}>
              {['Fecha','Medicamento','Tipo','Cantidad','Nuevo stock','Usuario','Motivo'].map(h=>(
                <th key={h} style={{padding:'8px 12px',fontSize:10,fontWeight:700,textTransform:'uppercase',
                  letterSpacing:'.4px',color:'var(--navy)',textAlign:'left',borderBottom:'1px solid var(--border)'}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cargando && <tr><td colSpan={7} style={{textAlign:'center',padding:20,color:'var(--text3)',fontSize:12}}>Cargando...</td></tr>}
            {!cargando && lista.length===0 && (
              <tr><td colSpan={7} style={{textAlign:'center',padding:20,color:'var(--text3)',fontSize:12}}>Sin ajustes registrados</td></tr>
            )}
            {lista.map(r=>(
              <tr key={r.COD+'-'+r.COD_MEDICAMENTO} style={{borderBottom:'1px solid var(--bg)'}}>
                <td style={{padding:'8px 12px',fontSize:12}}>{r.FECHA?.slice(0,16).replace('T',' ')}</td>
                <td style={{padding:'8px 12px',fontSize:12,fontWeight:600}}>{r.MEDICAMENTO}</td>
                <td style={{padding:'8px 12px',fontSize:11}}>
                  <span style={{display:'inline-flex',alignItems:'center',padding:'3px 8px',borderRadius:20,fontSize:10,fontWeight:600,
                    background:r.TIPO==='AJP'?'var(--green-lt)':'var(--red-lt)', color:r.TIPO==='AJP'?'var(--green)':'var(--red)'}}>
                    {r.TIPO==='AJP'?'➕ Positivo':'➖ Negativo'}
                  </span>
                </td>
                <td style={{padding:'8px 12px',fontSize:12}}>{r.CANTIDAD}</td>
                <td style={{padding:'8px 12px',fontSize:12}}>{r.NUEVO_STOCK_MEDICAMENTO}</td>
                <td style={{padding:'8px 12px',fontSize:12}}>{r.USUARIO}</td>
                <td style={{padding:'8px 12px',fontSize:12,color:'var(--text3)'}}>{r.OBSERVACIONES}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
/* ── Panel de ingresos de compra ── */
let _uidSeq = 0;
const genUid = () => `it_${Date.now().toString(36)}_${(++_uidSeq)}`;

function PanelCompras() {
  const [lista, setLista]   = useState([]);
  const [cargando, setCargando] = useState(true);
  const [proveedores, setProveedores] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showDetalle, setShowDetalle] = useState(null);
  const [form, setForm] = useState({ COD_PROVEEDOR:'', FECHA:'', C_COMPROBANTE:'FCT', NUMERO_COMPROBANTE:'', OBSERVACIONES:'', COD_PEDIDO_COMPRA:'', items:[] });
  const [pedidosProveedor, setPedidosProveedor] = useState([]);
  const [busqMed, setBusqMed] = useState('');
  const [sugsMed, setSugsMed] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState('');
  const [importando, setImportando] = useState(false);
  const [proveedorSugerido, setProveedorSugerido] = useState(null);
  const [creandoProveedor, setCreandoProveedor] = useState(false);
  const [asignandoUid, setAsignandoUid] = useState(null);

  const cargarFactor = async (uid, codMedicamento) => {
    try {
      const qs = new URLSearchParams({ cod_medicamento: codMedicamento });
      if (form.COD_PROVEEDOR) qs.set('cod_proveedor', form.COD_PROVEEDOR);
      const { data } = await api.get(`/farmacia/factor-conversion?${qs.toString()}`);
      setForm(f => ({ ...f, items: f.items.map(it => it._uid===uid ? { ...it, _factor: data.UNIDADES_POR_CAJA||1, _factorOrigen: data.ORIGEN } : it) }));
    } catch { /* si falla, se queda en factor 1 → solo modo Unidades tiene sentido */ }
  };

  const cargar = useCallback(async () => {
    setCargando(true);
    try { const { data } = await api.get('/farmacia/compras'); setLista(data||[]); }
    catch (e) { console.error(e); }
    setCargando(false);
  }, []);
  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { api.get('/proveedores?activos=1').then(r=>setProveedores(r.data||[])).catch(()=>{}); }, []);

  useEffect(() => {
    if (busqMed.length < 2) { setSugsMed([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/historia/medicamentos?q=${encodeURIComponent(busqMed)}`); setSugsMed(data||[]); } catch { setSugsMed([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [busqMed]);

  const abrirNuevo = () => {
    const hoy = new Date().toISOString().slice(0, 10);
    setForm({ COD_PROVEEDOR:'', FECHA: hoy, C_COMPROBANTE:'FCT', NUMERO_COMPROBANTE:'', OBSERVACIONES:'', COD_PEDIDO_COMPRA:'', items:[] });
    setPedidosProveedor([]); setErr(''); setShowForm(true);
  };
  const onProveedor = async (cod) => {
    setForm(f => ({ ...f, COD_PROVEEDOR: cod, COD_PEDIDO_COMPRA:'' }));
    if (!cod) { setPedidosProveedor([]); return; }
    try {
      const [rPen, rPar] = await Promise.all([
        api.get(`/farmacia/pedidos?estado=PEN&cod_proveedor=${cod}`),
        api.get(`/farmacia/pedidos?estado=PAR&cod_proveedor=${cod}`),
      ]);
      setPedidosProveedor([...(rPen.data||[]), ...(rPar.data||[])]);
    } catch { setPedidosProveedor([]); }
    // Re-consultar el factor de cada ítem ya agregado, por si este proveedor tiene un override distinto
    setForm(f => {
      setTimeout(() => f.items.forEach(it => it.COD_MEDICAMENTO && cargarFactor(it._uid, it.COD_MEDICAMENTO)), 0);
      return f;
    });
  };
  const crearProveedorSugerido = async () => {
    if (!proveedorSugerido) return;
    setCreandoProveedor(true); setErr('');
    try {
      const { data } = await api.post('/proveedores', {
        RAZON_SOCIAL: proveedorSugerido.RAZON_SOCIAL,
        IDENTIFICACION: proveedorSugerido.RUC,
      });
      const nuevo = { COD: data.COD, RAZON_SOCIAL: proveedorSugerido.RAZON_SOCIAL };
      setProveedores(ps => [...ps, nuevo].sort((a,b)=>a.RAZON_SOCIAL.localeCompare(b.RAZON_SOCIAL)));
      await onProveedor(data.COD);
      setProveedorSugerido(null);
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    setCreandoProveedor(false);
  };
  const cargarPendientesDelPedido = async () => {
    if (!form.COD_PEDIDO_COMPRA) return;
    try {
      const { data } = await api.get(`/farmacia/pedidos/${form.COD_PEDIDO_COMPRA}`);
      const nuevos = (data.items||[])
        .filter(it => parseFloat(it.CANTIDAD) > parseFloat(it.CANTIDAD_RECIBIDA))
        .map(it => ({
          COD_MEDICAMENTO: it.COD_MEDICAMENTO, MEDICAMENTO: it.MEDICAMENTO,
          CANTIDAD: (parseFloat(it.CANTIDAD) - parseFloat(it.CANTIDAD_RECIBIDA)).toString(),
          VALOR_UNITARIO: it.COSTO_ESTIMADO || '', LOTE:'', FECHA_VENCIMIENTO_LOTE:'',
          _pendienteMax: parseFloat(it.CANTIDAD) - parseFloat(it.CANTIDAD_RECIBIDA),
          _costoEstimado: parseFloat(it.COSTO_ESTIMADO) || null,
          _uid: genUid(),
        }));
      setForm(f => ({ ...f, items: [...f.items.filter(it=>!nuevos.some(n=>n.COD_MEDICAMENTO===it.COD_MEDICAMENTO)), ...nuevos] }));
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };
  const agregarItem = (m) => {
    if (asignandoUid) {
      setForm(f => ({ ...f, items: f.items.map(it=>it._uid===asignandoUid
        ? { ...it, COD_MEDICAMENTO:m.COD, MEDICAMENTO:m.MEDICAMENTO, _sinEmparejar:false }
        : it) }));
      cargarFactor(asignandoUid, m.COD);
      setAsignandoUid(null); setBusqMed(''); setSugsMed([]);
      return;
    }
    if (form.items.some(it=>it.COD_MEDICAMENTO===m.COD)) { setBusqMed(''); setSugsMed([]); return; }
    const uid = genUid();
    setForm(f => ({ ...f, items: [...f.items, { _uid:uid, COD_MEDICAMENTO:m.COD, MEDICAMENTO:m.MEDICAMENTO,
      CANTIDAD:1, VALOR_UNITARIO:'', LOTE:'', FECHA_VENCIMIENTO_LOTE:'',
      _modo:'unidad', CAJAS_INGRESADAS:'', VALOR_CAJA:'', _factor:1, _factorOrigen:null }] }));
    cargarFactor(uid, m.COD);
    setBusqMed(''); setSugsMed([]);
  };
  const quitarItem = (uid) => setForm(f => ({ ...f, items: f.items.filter(it=>it._uid!==uid) }));
  const setItemCampo = (uid, campo, v) => setForm(f => ({ ...f, items: f.items.map(it=>it._uid===uid?{...it,[campo]:v}:it) }));
  const setItemModo = (uid, modo) => setForm(f => ({ ...f, items: f.items.map(it=>it._uid===uid?{...it,_modo:modo}:it) }));
  const setItemCajas = (uid, val) => setForm(f => ({ ...f, items: f.items.map(it=>{
    if (it._uid!==uid) return it;
    const cajas = parseFloat(val) || 0;
    const factor = it._factor || 1;
    const costoCaja = parseFloat(it.VALOR_CAJA) || 0;
    return { ...it, CAJAS_INGRESADAS: val,
      CANTIDAD: cajas>0 ? String(cajas*factor) : '',
      VALOR_UNITARIO: (costoCaja>0 && factor>0) ? (costoCaja/factor).toFixed(4) : it.VALOR_UNITARIO };
  }) }));
  const setItemCostoCaja = (uid, val) => setForm(f => ({ ...f, items: f.items.map(it=>{
    if (it._uid!==uid) return it;
    const factor = it._factor || 1;
    const costoCaja = parseFloat(val) || 0;
    return { ...it, VALOR_CAJA: val,
      VALOR_UNITARIO: (costoCaja>0 && factor>0) ? (costoCaja/factor).toFixed(4) : it.VALOR_UNITARIO };
  }) }));
  const pedirAsignar = (uid) => {
    setAsignandoUid(uid); setBusqMed(''); setSugsMed([]);
  };
  const cancelarAsignar = () => { setAsignandoUid(null); setBusqMed(''); setSugsMed([]); };
  const onSeleccionarXml = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setImportando(true); setErr(''); setProveedorSugerido(null);
    try {
      const fd = new FormData();
      fd.append('xml', file);
      const { data } = await api.post('/farmacia/importar-xml-compra', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const nuevos = (data.items||[]).map(it => ({
        _uid: genUid(),
        COD_MEDICAMENTO: it.COD_MEDICAMENTO || null,
        MEDICAMENTO: it.MEDICAMENTO || it.DESCRIPCION_PROVEEDOR,
        CANTIDAD: it.CANTIDAD, VALOR_UNITARIO: it.VALOR_UNITARIO, LOTE:'', FECHA_VENCIMIENTO_LOTE:'',
        CODIGO_PROVEEDOR: it.CODIGO_PROVEEDOR, DESCRIPCION_PROVEEDOR: it.DESCRIPCION_PROVEEDOR,
        _sinEmparejar: !it.COD_MEDICAMENTO,
      }));
      setForm(f => ({
        ...f,
        FECHA: data.FECHA || f.FECHA,
        NUMERO_COMPROBANTE: data.NUMERO_COMPROBANTE || f.NUMERO_COMPROBANTE,
        items: [...f.items, ...nuevos],
      }));
      if (data.proveedor) onProveedor(data.proveedor.COD);
      else if (data.proveedorSugerido) setProveedorSugerido(data.proveedorSugerido);
    } catch (err2) { setErr(err2.response?.data?.error || err2.message); }
    setImportando(false);
  };

  const guardar = async () => {
    if (!form.items.length) { setErr('Agrega al menos un ítem'); return; }
    if (form.items.some(it=>it._sinEmparejar)) { setErr('Hay ítems importados sin emparejar con un medicamento del catálogo'); return; }
    for (const it of form.items) {
      if (!it.CANTIDAD || !it.VALOR_UNITARIO) { setErr(`Falta cantidad o valor unitario en "${it.MEDICAMENTO}"`); return; }
    }
    setSaving(true); setErr('');
    try {
      await api.post('/farmacia/compras', { ...form, COD_PEDIDO_COMPRA: form.COD_PEDIDO_COMPRA || null });
      setShowForm(false);
      cargar();
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    setSaving(false);
  };
  const verDetalle = async (c) => {
    try { const { data } = await api.get(`/farmacia/compras/${c.COD}`); setShowDetalle(data); }
    catch (e) { alert(e.response?.data?.error || e.message); }
  };

  return (
    <div>
      <div style={{display:'flex',gap:10,marginBottom:14,alignItems:'center'}}>
        <button className="btn btn-primary btn-sm" onClick={abrirNuevo}>+ Nueva compra</button>
      </div>
      <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,overflow:'hidden'}}>
        <table style={{width:'100%',borderCollapse:'collapse'}}>
          <thead>
            <tr style={{background:'var(--blue-lt)'}}>
              {['#','Fecha','Proveedor','Comprobante','Ítems','Total',''].map(h=>(
                <th key={h} style={{padding:'8px 12px',fontSize:10,fontWeight:700,textTransform:'uppercase',
                  letterSpacing:'.4px',color:'var(--navy)',textAlign:'left',borderBottom:'1px solid var(--border)'}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cargando && <tr><td colSpan={7} style={{textAlign:'center',padding:20,color:'var(--text3)',fontSize:12}}>Cargando...</td></tr>}
            {!cargando && lista.length===0 && (
              <tr><td colSpan={7} style={{textAlign:'center',padding:20,color:'var(--text3)',fontSize:12}}>Sin compras registradas</td></tr>
            )}
            {lista.map(c=>(
              <tr key={c.COD} style={{borderBottom:'1px solid var(--bg)'}}>
                <td style={{padding:'8px 12px',fontSize:12,color:'var(--text3)'}}>#{c.COD}</td>
                <td style={{padding:'8px 12px',fontSize:12}}>{c.FECHA?.slice(0,10)}</td>
                <td style={{padding:'8px 12px',fontSize:12,fontWeight:600}}>{c.PROVEEDOR||'—'}</td>
                <td style={{padding:'8px 12px',fontSize:12}}>{c.C_COMPROBANTE} {c.NUMERO_COMPROBANTE||''}</td>
                <td style={{padding:'8px 12px',fontSize:12}}>{c.TOTAL_ITEMS}</td>
                <td style={{padding:'8px 12px',fontSize:12,fontWeight:600}}>${parseFloat(c.TOTAL||0).toFixed(2)}</td>
                <td style={{padding:'8px 12px',fontSize:11}}>
                  <button className="btn btn-sm" onClick={()=>verDetalle(c)}>Ver</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div onClick={e=>e.target===e.currentTarget&&setShowForm(false)}
          style={{position:'fixed',inset:0,background:'rgba(10,18,32,.5)',display:'flex',alignItems:'center',justifyContent:'center',padding:24,zIndex:500}}>
          <div style={{background:'var(--surface)',borderRadius:14,width:'100%',maxWidth:1080,maxHeight:'88vh',
            display:'flex',flexDirection:'column',overflow:'hidden',boxShadow:'0 24px 60px -12px rgba(10,18,32,.35)'}}>
            <div style={{background:'var(--navy)',color:'var(--surface)',padding:'16px 22px',
              display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
              <span style={{fontWeight:700,fontSize:13}}>Nueva compra</span>
              <span style={{cursor:'pointer',fontSize:18,opacity:.8}} onClick={()=>setShowForm(false)}>&#x2715;</span>
            </div>
            <div style={{overflowY:'auto',padding:22,flex:1}}>
              <div style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr 1fr',gap:10,marginBottom:10}}>
                <div>
                  <label className="form-label" style={{fontSize:10}}>Proveedor</label>
                  <select className="form-control" style={{fontSize:12}} value={form.COD_PROVEEDOR}
                    onChange={e=>onProveedor(e.target.value)}>
                    <option value="">Sin especificar</option>
                    {proveedores.map(p=><option key={p.COD} value={p.COD}>{p.RAZON_SOCIAL}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label" style={{fontSize:10}}>Fecha</label>
                  <input type="date" className="form-control" style={{fontSize:12}} value={form.FECHA}
                    max={new Date().toISOString().slice(0,10)}
                    onChange={e=>setForm(f=>({...f,FECHA:e.target.value}))} />
                </div>
                <div>
                  <label className="form-label" style={{fontSize:10}}>Comprobante</label>
                  <select className="form-control" style={{fontSize:12}} value={form.C_COMPROBANTE}
                    onChange={e=>setForm(f=>({...f,C_COMPROBANTE:e.target.value}))}>
                    <option value="FCT">Factura</option>
                    <option value="DNC">Donación</option>
                    <option value="NNG">Ninguno</option>
                  </select>
                </div>
                <div>
                  <label className="form-label" style={{fontSize:10}}>N° comprobante</label>
                  <input className="form-control" style={{fontSize:12}} value={form.NUMERO_COMPROBANTE}
                    onChange={e=>setForm(f=>({...f,NUMERO_COMPROBANTE:e.target.value}))} />
                </div>
              </div>
              {pedidosProveedor.length>0 && (
                <div style={{display:'flex',gap:8,alignItems:'flex-end',marginBottom:10,background:'var(--surface2)',padding:10,borderRadius:8}}>
                  <div style={{flex:1}}>
                    <label className="form-label" style={{fontSize:10}}>Recibir contra pedido de compra (opcional)</label>
                    <select className="form-control" style={{fontSize:12}} value={form.COD_PEDIDO_COMPRA}
                      onChange={e=>setForm(f=>({...f,COD_PEDIDO_COMPRA:e.target.value}))}>
                      <option value="">Ninguno — compra libre</option>
                      {pedidosProveedor.map(p=><option key={p.COD} value={p.COD}>#{p.COD} — {p.FECHA_PEDIDO?.slice(0,10)} ({p.TOTAL_RECIBIDO||0}/{p.TOTAL_CANTIDAD||0} recibido)</option>)}
                    </select>
                  </div>
                  <button className="btn btn-sm" disabled={!form.COD_PEDIDO_COMPRA} onClick={cargarPendientesDelPedido}>Cargar pendientes</button>
                </div>
              )}
              <div style={{marginBottom:10}}>
                <label className="form-control-label" style={{display:'inline-block',cursor:importando?'default':'pointer'}}>
                  <span className="btn btn-sm" style={{opacity:importando?.6:1}}>{importando ? 'Importando...' : '📄 Importar XML de factura'}</span>
                  <input type="file" accept=".xml" style={{display:'none'}} disabled={importando} onChange={onSeleccionarXml} />
                </label>
                <span style={{fontSize:10,color:'var(--text3)',marginLeft:8}}>Sube el XML que te envió el proveedor con la factura electrónica</span>
              </div>
              {proveedorSugerido && (
                <div style={{fontSize:11,color:'var(--amber)',background:'var(--amber-lt)',padding:'8px 10px',borderRadius:6,marginBottom:10,border:'1px solid var(--amber)',display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,flexWrap:'wrap'}}>
                  <span>
                    El XML es de <b>{proveedorSugerido.RAZON_SOCIAL}</b> (RUC {proveedorSugerido.RUC}), que no está en tu catálogo de proveedores.
                  </span>
                  <button type="button" className="btn btn-sm" disabled={creandoProveedor} onClick={crearProveedorSugerido}
                    style={{whiteSpace:'nowrap'}}>
                    {creandoProveedor ? 'Creando...' : '+ Crear proveedor con estos datos'}
                  </button>
                </div>
              )}
              <div style={{marginBottom:10,position:'relative'}}>
                <label className="form-label" style={{fontSize:10}}>
                  {asignandoUid ? 'Asignar medicamento del catálogo' : 'Agregar medicamento manualmente'}
                  {asignandoUid && <span style={{marginLeft:8,cursor:'pointer',color:'var(--red)',fontWeight:400}} onClick={cancelarAsignar}>cancelar</span>}
                </label>
                <input className="form-control" style={{fontSize:12}} placeholder="Buscar medicamento..."
                  value={busqMed} onChange={e=>setBusqMed(e.target.value)} />
                {sugsMed.length>0 && (
                  <div style={{position:'absolute',top:'100%',left:0,right:0,background:'var(--surface)',border:'1px solid var(--border)',
                    borderRadius:5,zIndex:10,maxHeight:160,overflowY:'auto',boxShadow:'0 4px 12px rgba(0,0,0,.1)'}}>
                    {sugsMed.map(m=>(
                      <div key={m.COD} onClick={()=>agregarItem(m)} style={{padding:'6px 10px',fontSize:12,cursor:'pointer',borderBottom:'1px solid var(--surface2)'}}
                        onMouseEnter={e=>e.currentTarget.style.background='var(--blue-lt)'}
                        onMouseLeave={e=>e.currentTarget.style.background=''}>
                        {m.MEDICAMENTO}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {form.items.length>0 && (
                <div style={{overflowX:'auto',marginBottom:10,border:'1px solid var(--border)',borderRadius:8}}>
                <table style={{width:'100%',minWidth:900,borderCollapse:'collapse'}}>
                  <thead>
                    <tr style={{background:'var(--blue-lt)'}}>
                      {['Medicamento','Modo','Cantidad / Cajas','Valor unit. / Costo caja','Conversión','Lote','Vencimiento',''].map(h=>(
                        <th key={h} style={{padding:'8px',fontSize:10,fontWeight:700,color:'var(--navy)',textAlign:'left',whiteSpace:'nowrap'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map(it=>{
                      const modoCajas = it._modo === 'caja';
                      const factor = it._factor || 1;
                      return (
                      <tr key={it._uid} style={{borderBottom:'1px solid var(--bg)',background:it._sinEmparejar?'var(--amber-lt)':undefined}}>
                        <td style={{padding:'8px',fontSize:12,minWidth:170}}>
                          {it._sinEmparejar ? (
                            <div>
                              <div style={{color:'var(--amber)'}}>⚠ {it.MEDICAMENTO || it.DESCRIPCION_PROVEEDOR}</div>
                              <span style={{fontSize:10,cursor:'pointer',textDecoration:'underline',color:'var(--blue)'}}
                                onClick={()=>pedirAsignar(it._uid)}>Asignar medicamento del catálogo</span>
                            </div>
                          ) : it.MEDICAMENTO}
                        </td>
                        <td style={{padding:'6px 8px'}}>
                          {!it._sinEmparejar && factor > 1 ? (
                            <select className="form-control" style={{fontSize:11,width:78}} value={it._modo||'unidad'}
                              onChange={e=>setItemModo(it._uid, e.target.value)}>
                              <option value="unidad">Unidades</option>
                              <option value="caja">Cajas</option>
                            </select>
                          ) : <span style={{fontSize:10,color:'var(--text3)'}}>—</span>}
                        </td>
                        <td style={{padding:'6px 8px'}}>
                          {modoCajas ? (
                            <input type="number" min="0" step="0.01" className="form-control" style={{fontSize:12,width:70}}
                              placeholder="cajas" value={it.CAJAS_INGRESADAS} onChange={e=>setItemCajas(it._uid,e.target.value)} />
                          ) : (
                            <>
                              <input type="number" min="0" step="0.01" className="form-control" style={{fontSize:12,width:70}}
                                max={it._pendienteMax ?? undefined}
                                value={it.CANTIDAD} onChange={e=>setItemCampo(it._uid,'CANTIDAD',e.target.value)} />
                              {it._pendienteMax != null && <div style={{fontSize:9,color:'var(--amber)'}}>máx {it._pendienteMax}</div>}
                            </>
                          )}
                        </td>
                        <td style={{padding:'6px 8px'}}>
                          {modoCajas ? (
                            <input type="number" min="0" step="0.01" className="form-control" style={{fontSize:12,width:80}}
                              placeholder="costo/caja" value={it.VALOR_CAJA} onChange={e=>setItemCostoCaja(it._uid,e.target.value)} />
                          ) : (
                            <>
                              <input type="number" min="0" step="0.01" className="form-control" style={{fontSize:12,width:80}}
                                max={it._costoEstimado ?? undefined}
                                value={it.VALOR_UNITARIO} onChange={e=>setItemCampo(it._uid,'VALOR_UNITARIO',e.target.value)} />
                              {it._costoEstimado != null && <div style={{fontSize:9,color:'var(--amber)'}}>máx ${it._costoEstimado}</div>}
                            </>
                          )}
                        </td>
                        <td style={{padding:'6px 8px',fontSize:11,color:'var(--text3)'}}>
                          {modoCajas ? (
                            <>
                              {(parseFloat(it.CAJAS_INGRESADAS)||0) > 0 ? `${(parseFloat(it.CAJAS_INGRESADAS)*factor).toFixed(2)} u` : '—'}
                              {it.VALOR_UNITARIO ? ` · $${parseFloat(it.VALOR_UNITARIO).toFixed(4)}/u` : ''}
                              <div style={{fontSize:9,color: it._factorOrigen==='proveedor' ? 'var(--blue-mid)' : 'var(--text3)'}}>
                                1 caja = {factor} u {it._factorOrigen==='proveedor' ? '(proveedor)' : '(catálogo)'}
                              </div>
                            </>
                          ) : '—'}
                        </td>
                        <td style={{padding:'6px 8px'}}>
                          <input className="form-control" style={{fontSize:12,width:80}}
                            value={it.LOTE} onChange={e=>setItemCampo(it._uid,'LOTE',e.target.value)} />
                        </td>
                        <td style={{padding:'6px 8px'}}>
                          <input type="date" className="form-control" style={{fontSize:12,width:130}}
                            value={it.FECHA_VENCIMIENTO_LOTE} onChange={e=>setItemCampo(it._uid,'FECHA_VENCIMIENTO_LOTE',e.target.value)} />
                        </td>
                        <td style={{padding:'6px 8px'}}>
                          <span style={{cursor:'pointer',color:'var(--red)'}} onClick={()=>quitarItem(it._uid)}>✕</span>
                        </td>
                      </tr>
                    );})}
                  </tbody>
                </table>
                </div>
              )}
              <div style={{marginBottom:10}}>
                <label className="form-label" style={{fontSize:10}}>Observaciones</label>
                <textarea className="form-control" style={{fontSize:12,resize:'vertical'}} rows={2}
                  value={form.OBSERVACIONES} onChange={e=>setForm(f=>({...f,OBSERVACIONES:e.target.value}))} />
              </div>
              {err && <div style={{color:'var(--red)',fontSize:11,marginBottom:10}}>{err}</div>}
              <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
                <button className="btn btn-sm" onClick={()=>setShowForm(false)}>Cancelar</button>
                <button className="btn btn-primary btn-sm" disabled={saving || form.items.some(it=>it._sinEmparejar)} onClick={guardar}>
                  {saving ? 'Guardando...' : 'Registrar compra'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDetalle && (
        <div onClick={e=>e.target===e.currentTarget&&setShowDetalle(null)}
          style={{position:'fixed',inset:0,background:'rgba(10,18,32,.5)',display:'flex',alignItems:'center',justifyContent:'center',padding:24,zIndex:500}}>
          <div style={{background:'var(--surface)',borderRadius:14,width:'100%',maxWidth:560,maxHeight:'88vh',
            display:'flex',flexDirection:'column',overflow:'hidden',boxShadow:'0 24px 60px -12px rgba(10,18,32,.35)'}}>
            <div style={{background:'var(--navy)',color:'var(--surface)',padding:'16px 22px',
              display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
              <span style={{fontWeight:700,fontSize:13}}>Compra #{showDetalle.COD} — {showDetalle.PROVEEDOR||'Sin proveedor'}</span>
              <span style={{cursor:'pointer',fontSize:18,opacity:.8}} onClick={()=>setShowDetalle(null)}>&#x2715;</span>
            </div>
            <div style={{overflowY:'auto',padding:22,flex:1}}>
              <div style={{fontSize:12,color:'var(--text3)',marginBottom:10}}>
                Fecha: {showDetalle.FECHA?.slice(0,10)} · {showDetalle.C_COMPROBANTE} {showDetalle.NUMERO_COMPROBANTE||''}
              </div>
              <table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead>
                  <tr style={{background:'var(--blue-lt)'}}>
                    {['Medicamento','Cantidad','Valor unit.','Total','Lote'].map(h=>(
                      <th key={h} style={{padding:'6px 8px',fontSize:10,fontWeight:700,color:'var(--navy)',textAlign:'left'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(showDetalle.items||[]).map(it=>(
                    <tr key={it.COD} style={{borderBottom:'1px solid var(--bg)'}}>
                      <td style={{padding:'6px 8px',fontSize:12}}>{it.MEDICAMENTO}</td>
                      <td style={{padding:'6px 8px',fontSize:12}}>{it.CANTIDAD}</td>
                      <td style={{padding:'6px 8px',fontSize:12}}>${parseFloat(it.VALOR_UNITARIO).toFixed(2)}</td>
                      <td style={{padding:'6px 8px',fontSize:12,fontWeight:600}}>${(it.CANTIDAD*it.VALOR_UNITARIO).toFixed(2)}</td>
                      <td style={{padding:'6px 8px',fontSize:12}}>{it.LOTE||'—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{display:'flex',justifyContent:'flex-end',marginTop:12}}>
                <button className="btn btn-sm" onClick={()=>setShowDetalle(null)}>Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
/* ── Panel de pedidos de compra ── */
function PanelPedidos() {
  const ESTADOS = { PEN:{l:'Pendiente',bg:'var(--amber-lt)',c:'var(--amber)'}, PAR:{l:'Parcial',bg:'var(--blue-lt)',c:'var(--blue-mid)'},
                     REC:{l:'Recibido',bg:'var(--green-lt)',c:'var(--green)'}, ANL:{l:'Anulado',bg:'var(--red-lt)',c:'var(--red)'} };
  const [lista, setLista]   = useState([]);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [cargando, setCargando] = useState(true);
  const [proveedores, setProveedores] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm]     = useState({ COD_PROVEEDOR:'', FECHA_PEDIDO:new Date().toISOString().slice(0,10), OBSERVACIONES:'', items:[] });
  const [busqMed, setBusqMed] = useState('');
  const [sugsMed, setSugsMed] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState('');
  const [showDetalle, setShowDetalle] = useState(null); // objeto pedido con items, o null

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await api.get(`/farmacia/pedidos${filtroEstado ? `?estado=${filtroEstado}` : ''}`);
      setLista(data || []);
    } catch (e) { console.error(e); }
    setCargando(false);
  }, [filtroEstado]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { api.get('/proveedores?activos=1').then(r=>setProveedores(r.data||[])).catch(()=>{}); }, []);

  useEffect(() => {
    if (busqMed.length < 2) { setSugsMed([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/historia/medicamentos?q=${encodeURIComponent(busqMed)}`); setSugsMed(data||[]); } catch { setSugsMed([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [busqMed]);

  const abrirNuevo = () => {
    setEditando(null);
    setForm({ COD_PROVEEDOR:'', FECHA_PEDIDO:new Date().toISOString().slice(0,10), OBSERVACIONES:'', items:[] });
    setErr(''); setShowForm(true);
  };
  const abrirEditar = async (p) => {
    try {
      const { data } = await api.get(`/farmacia/pedidos/${p.COD}`);
      setEditando(p.COD);
      setForm({
        COD_PROVEEDOR: data.COD_PROVEEDOR, FECHA_PEDIDO: data.FECHA_PEDIDO?.slice(0,10),
        OBSERVACIONES: data.OBSERVACIONES||'',
        items: (data.items||[]).map(it=>({ COD_MEDICAMENTO:it.COD_MEDICAMENTO, MEDICAMENTO:it.MEDICAMENTO, CANTIDAD:it.CANTIDAD, COSTO_ESTIMADO:it.COSTO_ESTIMADO })),
      });
      setErr(''); setShowForm(true);
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };
  const verDetalle = async (p) => {
    try { const { data } = await api.get(`/farmacia/pedidos/${p.COD}`); setShowDetalle(data); }
    catch (e) { alert(e.response?.data?.error || e.message); }
  };
  const anular = async (p) => {
    if (!window.confirm(`¿Anular el pedido #${p.COD} a ${p.PROVEEDOR}?`)) return;
    try { await api.patch(`/farmacia/pedidos/${p.COD}/anular`); cargar(); }
    catch (e) { alert(e.response?.data?.error || e.message); }
  };
  const agregarItem = (m) => {
    if (form.items.some(it=>it.COD_MEDICAMENTO===m.COD)) { setBusqMed(''); setSugsMed([]); return; }
    setForm(f => ({ ...f, items: [...f.items, { COD_MEDICAMENTO:m.COD, MEDICAMENTO:m.MEDICAMENTO, CANTIDAD:1, COSTO_ESTIMADO:'' }] }));
    setBusqMed(''); setSugsMed([]);
  };
  const quitarItem = (cod) => setForm(f => ({ ...f, items: f.items.filter(it=>it.COD_MEDICAMENTO!==cod) }));
  const setItemCampo = (cod, campo, v) => setForm(f => ({ ...f, items: f.items.map(it=>it.COD_MEDICAMENTO===cod?{...it,[campo]:v}:it) }));

  const guardar = async () => {
    if (!form.COD_PROVEEDOR) { setErr('Selecciona un proveedor'); return; }
    if (!form.items.length) { setErr('Agrega al menos un ítem'); return; }
    setSaving(true); setErr('');
    try {
      if (editando) await api.put(`/farmacia/pedidos/${editando}`, form);
      else await api.post('/farmacia/pedidos', form);
      setShowForm(false);
      cargar();
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    setSaving(false);
  };

  return (
    <div>
      <div style={{display:'flex',gap:10,marginBottom:14,alignItems:'center'}}>
        <select className="form-control" style={{fontSize:12,maxWidth:180}} value={filtroEstado} onChange={e=>setFiltroEstado(e.target.value)}>
          <option value="">Todos los estados</option>
          <option value="PEN">Pendiente</option>
          <option value="PAR">Parcial</option>
          <option value="REC">Recibido</option>
          <option value="ANL">Anulado</option>
        </select>
        <button className="btn btn-primary btn-sm" onClick={abrirNuevo}>+ Nuevo pedido</button>
      </div>
      <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,overflow:'hidden'}}>
        <table style={{width:'100%',borderCollapse:'collapse'}}>
          <thead>
            <tr style={{background:'var(--blue-lt)'}}>
              {['#','Fecha','Proveedor','Ítems','Cant. pedida/recibida','Estado',''].map(h=>(
                <th key={h} style={{padding:'8px 12px',fontSize:10,fontWeight:700,textTransform:'uppercase',
                  letterSpacing:'.4px',color:'var(--navy)',textAlign:'left',borderBottom:'1px solid var(--border)'}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cargando && <tr><td colSpan={7} style={{textAlign:'center',padding:20,color:'var(--text3)',fontSize:12}}>Cargando...</td></tr>}
            {!cargando && lista.length===0 && (
              <tr><td colSpan={7} style={{textAlign:'center',padding:20,color:'var(--text3)',fontSize:12}}>Sin pedidos de compra registrados</td></tr>
            )}
            {lista.map(p=>(
              <tr key={p.COD} style={{borderBottom:'1px solid var(--bg)'}}>
                <td style={{padding:'8px 12px',fontSize:12,color:'var(--text3)'}}>#{p.COD}</td>
                <td style={{padding:'8px 12px',fontSize:12}}>{p.FECHA_PEDIDO?.slice(0,10)}</td>
                <td style={{padding:'8px 12px',fontSize:12,fontWeight:600}}>{p.PROVEEDOR}</td>
                <td style={{padding:'8px 12px',fontSize:12}}>{p.TOTAL_ITEMS}</td>
                <td style={{padding:'8px 12px',fontSize:12}}>{p.TOTAL_RECIBIDO||0} / {p.TOTAL_CANTIDAD||0}</td>
                <td style={{padding:'8px 12px',fontSize:11}}>
                  <span style={{display:'inline-flex',alignItems:'center',padding:'3px 8px',borderRadius:20,fontSize:10,fontWeight:600,
                    background:ESTADOS[p.C_ESTADO]?.bg,color:ESTADOS[p.C_ESTADO]?.c}}>
                    {ESTADOS[p.C_ESTADO]?.l || p.C_ESTADO}
                  </span>
                </td>
                <td style={{padding:'8px 12px',fontSize:11,whiteSpace:'nowrap'}}>
                  <button className="btn btn-sm" style={{marginRight:6}} onClick={()=>verDetalle(p)}>Ver</button>
                  {p.C_ESTADO==='PEN' && <button className="btn btn-sm" style={{marginRight:6}} onClick={()=>abrirEditar(p)}>Editar</button>}
                  {(p.C_ESTADO==='PEN'||p.C_ESTADO==='PAR') && <button className="btn btn-sm" style={{color:'var(--red)',borderColor:'var(--red)'}} onClick={()=>anular(p)}>Anular</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div onClick={e=>e.target===e.currentTarget&&setShowForm(false)}
          style={{position:'fixed',inset:0,background:'rgba(10,18,32,.5)',display:'flex',alignItems:'center',justifyContent:'center',padding:24,zIndex:500}}>
          <div style={{background:'var(--surface)',borderRadius:14,width:'100%',maxWidth:640,maxHeight:'88vh',
            display:'flex',flexDirection:'column',overflow:'hidden',boxShadow:'0 24px 60px -12px rgba(10,18,32,.35)'}}>
            <div style={{background:'var(--navy)',color:'var(--surface)',padding:'16px 22px',
              display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
              <span style={{fontWeight:700,fontSize:13}}>{editando ? `Editar pedido #${editando}` : 'Nuevo pedido de compra'}</span>
              <span style={{cursor:'pointer',fontSize:18,opacity:.8}} onClick={()=>setShowForm(false)}>&#x2715;</span>
            </div>
            <div style={{overflowY:'auto',padding:22,flex:1}}>
              <div style={{display:'grid',gridTemplateColumns:'2fr 1fr',gap:10,marginBottom:10}}>
                <div>
                  <label className="form-label" style={{fontSize:10}}>Proveedor *</label>
                  <select className="form-control" style={{fontSize:12}} value={form.COD_PROVEEDOR}
                    onChange={e=>setForm(f=>({...f,COD_PROVEEDOR:e.target.value}))}>
                    <option value="">Seleccione...</option>
                    {proveedores.map(p=><option key={p.COD} value={p.COD}>{p.RAZON_SOCIAL}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label" style={{fontSize:10}}>Fecha</label>
                  <input type="date" className="form-control" style={{fontSize:12}} value={form.FECHA_PEDIDO}
                    onChange={e=>setForm(f=>({...f,FECHA_PEDIDO:e.target.value}))} />
                </div>
              </div>
              <div style={{marginBottom:10,position:'relative'}}>
                <label className="form-label" style={{fontSize:10}}>Agregar medicamento</label>
                <input className="form-control" style={{fontSize:12}} placeholder="Buscar medicamento..."
                  value={busqMed} onChange={e=>setBusqMed(e.target.value)} />
                {sugsMed.length>0 && (
                  <div style={{position:'absolute',top:'100%',left:0,right:0,background:'var(--surface)',border:'1px solid var(--border)',
                    borderRadius:5,zIndex:10,maxHeight:160,overflowY:'auto',boxShadow:'0 4px 12px rgba(0,0,0,.1)'}}>
                    {sugsMed.map(m=>(
                      <div key={m.COD} onClick={()=>agregarItem(m)} style={{padding:'6px 10px',fontSize:12,cursor:'pointer',borderBottom:'1px solid var(--surface2)'}}
                        onMouseEnter={e=>e.currentTarget.style.background='var(--blue-lt)'}
                        onMouseLeave={e=>e.currentTarget.style.background=''}>
                        {m.MEDICAMENTO}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {form.items.length>0 && (
                <table style={{width:'100%',borderCollapse:'collapse',marginBottom:10}}>
                  <thead>
                    <tr style={{background:'var(--blue-lt)'}}>
                      {['Medicamento','Cantidad','Costo estimado',''].map(h=>(
                        <th key={h} style={{padding:'6px 8px',fontSize:10,fontWeight:700,color:'var(--navy)',textAlign:'left'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map(it=>(
                      <tr key={it.COD_MEDICAMENTO} style={{borderBottom:'1px solid var(--bg)'}}>
                        <td style={{padding:'6px 8px',fontSize:12}}>{it.MEDICAMENTO}</td>
                        <td style={{padding:'6px 8px'}}>
                          <input type="number" min="0" step="0.01" className="form-control" style={{fontSize:12,width:80}}
                            value={it.CANTIDAD} onChange={e=>setItemCampo(it.COD_MEDICAMENTO,'CANTIDAD',e.target.value)} />
                        </td>
                        <td style={{padding:'6px 8px'}}>
                          <input type="number" min="0" step="0.01" className="form-control" style={{fontSize:12,width:100}}
                            value={it.COSTO_ESTIMADO} onChange={e=>setItemCampo(it.COD_MEDICAMENTO,'COSTO_ESTIMADO',e.target.value)} />
                        </td>
                        <td style={{padding:'6px 8px'}}>
                          <span style={{cursor:'pointer',color:'var(--red)'}} onClick={()=>quitarItem(it.COD_MEDICAMENTO)}>✕</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <div style={{marginBottom:10}}>
                <label className="form-label" style={{fontSize:10}}>Observaciones</label>
                <textarea className="form-control" style={{fontSize:12,resize:'vertical'}} rows={2}
                  value={form.OBSERVACIONES} onChange={e=>setForm(f=>({...f,OBSERVACIONES:e.target.value}))} />
              </div>
              {err && <div style={{color:'var(--red)',fontSize:11,marginBottom:10}}>{err}</div>}
              <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
                <button className="btn btn-sm" onClick={()=>setShowForm(false)}>Cancelar</button>
                <button className="btn btn-primary btn-sm" disabled={saving} onClick={guardar}>
                  {saving ? 'Guardando...' : 'Guardar pedido'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDetalle && (
        <div onClick={e=>e.target===e.currentTarget&&setShowDetalle(null)}
          style={{position:'fixed',inset:0,background:'rgba(10,18,32,.5)',display:'flex',alignItems:'center',justifyContent:'center',padding:24,zIndex:500}}>
          <div style={{background:'var(--surface)',borderRadius:14,width:'100%',maxWidth:560,maxHeight:'88vh',
            display:'flex',flexDirection:'column',overflow:'hidden',boxShadow:'0 24px 60px -12px rgba(10,18,32,.35)'}}>
            <div style={{background:'var(--navy)',color:'var(--surface)',padding:'16px 22px',
              display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
              <span style={{fontWeight:700,fontSize:13}}>Pedido #{showDetalle.COD} — {showDetalle.PROVEEDOR}</span>
              <span style={{cursor:'pointer',fontSize:18,opacity:.8}} onClick={()=>setShowDetalle(null)}>&#x2715;</span>
            </div>
            <div style={{overflowY:'auto',padding:22,flex:1}}>
              <div style={{fontSize:12,color:'var(--text3)',marginBottom:10}}>
                Fecha: {showDetalle.FECHA_PEDIDO?.slice(0,10)} · Estado: {ESTADOS[showDetalle.C_ESTADO]?.l || showDetalle.C_ESTADO}
                {showDetalle.OBSERVACIONES && <> · {showDetalle.OBSERVACIONES}</>}
              </div>
              <table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead>
                  <tr style={{background:'var(--blue-lt)'}}>
                    {['Medicamento','Pedido','Recibido','Pendiente'].map(h=>(
                      <th key={h} style={{padding:'6px 8px',fontSize:10,fontWeight:700,color:'var(--navy)',textAlign:'left'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(showDetalle.items||[]).map(it=>(
                    <tr key={it.COD} style={{borderBottom:'1px solid var(--bg)'}}>
                      <td style={{padding:'6px 8px',fontSize:12}}>{it.MEDICAMENTO}</td>
                      <td style={{padding:'6px 8px',fontSize:12}}>{it.CANTIDAD}</td>
                      <td style={{padding:'6px 8px',fontSize:12}}>{it.CANTIDAD_RECIBIDA}</td>
                      <td style={{padding:'6px 8px',fontSize:12,fontWeight:600}}>{(it.CANTIDAD-it.CANTIDAD_RECIBIDA).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{display:'flex',justifyContent:'flex-end',marginTop:12}}>
                <button className="btn btn-sm" onClick={()=>setShowDetalle(null)}>Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
/* ── Panel de proveedores ── */
/* ── Panel de Catálogo de medicamentos ── */
function PanelCatalogo() {
  const VACIO = { MEDICAMENTO:'', NOMBRE_COMERCIAL:'', COD_SUBGRUPO:'', COD_CUADRO_BASICO:'',
    COD_UNIDAD:'', UNIDADES_POR_CAJA:'1', STOCK:'0', STOCK_MINIMO:'0', COSTO_PROMEDIO:'0', COSTO_REPOSICION:'0',
    COD_BARRAS:'', SE_PUEDE_VENDER:1, PORCENTAJE_UTILIDAD:'0', PRECIO_VENTA:'0',
    PRECIO_SUGERIDO:'', PRECIO_VALIDADO:1, ACTIVO:1 };
  const [lista, setLista]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [pagina, setPagina]   = useState(1);
  const [tam, setTam]         = useState(20);
  const [busq, setBusq]       = useState('');
  const [filtroVend, setFiltroVend] = useState('todos'); // todos | 1 | 0
  const [filtroSub, setFiltroSub]   = useState('');
  const [subgrupos, setSubgrupos] = useState([]);
  const [unidades, setUnidades]   = useState([]);
  const [cargando, setCargando] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editando, setEditando] = useState(null); // COD si se está editando, null si es nuevo
  const [form, setForm]     = useState(VACIO);
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState('');
  const [showAdmin, setShowAdmin] = useState(false);
  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const qs = new URLSearchParams();
      if (busq) qs.set('q', busq);
      if (filtroVend !== 'todos') qs.set('vendible', filtroVend);
      if (filtroSub) qs.set('subgrupo', filtroSub);
      qs.set('activo', 'todos');
      qs.set('page', pagina);
      qs.set('pageSize', tam);
      const { data } = await api.get(`/farmacia/medicamentos?${qs.toString()}`);
      setLista(data?.rows || []);
      setTotal(data?.total || 0);
    } catch (e) { console.error(e); }
    setCargando(false);
  }, [busq, filtroVend, filtroSub, pagina, tam]);
  useEffect(() => {
    const t = setTimeout(cargar, 300);
    return () => clearTimeout(t);
  }, [cargar]);
  useEffect(() => { setPagina(1); }, [busq, filtroVend, filtroSub, tam]);
  useEffect(() => {
    api.get('/farmacia/subgrupos').then(({data}) => setSubgrupos(data||[])).catch(()=>{});
    api.get('/farmacia/unidades').then(({data}) => setUnidades(data||[])).catch(()=>{});
  }, []);
  const totalPaginas = Math.max(1, Math.ceil(total / tam));
  const iniFila = total === 0 ? 0 : (pagina - 1) * tam + 1;
  const finFila = Math.min(pagina * tam, total);
  const numsPagina = () => {
    const start = Math.max(1, pagina - 2), end = Math.min(totalPaginas, pagina + 2);
    const nums = [];
    for (let p = start; p <= end; p++) nums.push(p);
    return nums;
  };
  const abrirNuevo = () => { setEditando(null); setForm(VACIO); setErr(''); setShowForm(true); };
  const abrirEditar = (m) => {
    setEditando(m.COD);
    setForm({
      MEDICAMENTO:m.MEDICAMENTO||'', NOMBRE_COMERCIAL:m.NOMBRE_COMERCIAL||'',
      COD_SUBGRUPO:m.COD_SUBGRUPO||'', COD_CUADRO_BASICO:m.COD_CUADRO_BASICO||'',
      COD_UNIDAD:m.COD_UNIDAD||'', UNIDADES_POR_CAJA:String(m.UNIDADES_POR_CAJA??1),
      STOCK:String(m.STOCK??0), STOCK_MINIMO:String(m.STOCK_MINIMO??0),
      COSTO_PROMEDIO:String(m.COSTO_PROMEDIO??0), COSTO_REPOSICION:String(m.COSTO_REPOSICION??0),
      COD_BARRAS:m.COD_BARRAS||'', SE_PUEDE_VENDER:m.SE_PUEDE_VENDER??1,
      PORCENTAJE_UTILIDAD:String(m.PORCENTAJE_UTILIDAD??0), PRECIO_VENTA:String(m.PRECIO_VENTA??0),
      PRECIO_SUGERIDO:m.PRECIO_SUGERIDO!=null?String(m.PRECIO_SUGERIDO):'', PRECIO_VALIDADO:m.PRECIO_VALIDADO??1,
      ACTIVO:m.ACTIVO??1
    });
    setErr(''); setShowForm(true);
  };
  const guardar = async () => {
    if (!form.MEDICAMENTO.trim()) { setErr('El nombre del medicamento es obligatorio'); return; }
    if (!form.COD_UNIDAD) { setErr('La unidad es obligatoria'); return; }
    setSaving(true); setErr('');
    try {
      if (editando) await api.put(`/farmacia/medicamentos/${editando}`, form);
      else await api.post('/farmacia/medicamentos', form);
      setShowForm(false);
      cargar();
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    setSaving(false);
  };
  return (
    <div>
      <div style={{display:'flex',gap:10,marginBottom:14,alignItems:'center',flexWrap:'wrap'}}>
        <input className="form-control" style={{fontSize:12,maxWidth:280}} placeholder="Buscar medicamento..."
          value={busq} onChange={e=>setBusq(e.target.value)} />
        <select className="form-control" style={{fontSize:12,maxWidth:220}} value={filtroVend}
          onChange={e=>setFiltroVend(e.target.value)}>
          <option value="todos">Todos</option>
          <option value="1">Solo vendibles</option>
          <option value="0">Solo vademécum (sin completar)</option>
        </select>
        <select className="form-control" style={{fontSize:12,maxWidth:220}} value={filtroSub}
          onChange={e=>setFiltroSub(e.target.value)}>
          <option value="">Todos los subgrupos</option>
          {subgrupos.map(s=>(
            <option key={s.COD} value={s.COD}>{s.GRUPO} / {s.SUBGRUPO}</option>
          ))}
        </select>
        <button className="btn btn-primary btn-sm" onClick={abrirNuevo}>+ Nuevo producto</button>
        <button className="btn btn-sm" onClick={()=>setShowAdmin(true)}>⚙️ Subgrupos y unidades</button>
      </div>
      <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,overflow:'hidden'}}>
        <table style={{width:'100%',borderCollapse:'collapse'}}>
          <thead>
            <tr style={{background:'var(--blue-lt)'}}>
              {['Medicamento','Subgrupo','Unidad','Stock','Precio venta','Estado',''].map(h=>(
                <th key={h} style={{padding:'8px 12px',fontSize:10,fontWeight:700,textTransform:'uppercase',
                  letterSpacing:'.4px',color:'var(--navy)',textAlign:'left',borderBottom:'1px solid var(--border)'}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cargando && <tr><td colSpan={7} style={{textAlign:'center',padding:20,color:'var(--text3)',fontSize:12}}>Cargando...</td></tr>}
            {!cargando && lista.length===0 && (
              <tr><td colSpan={7} style={{textAlign:'center',padding:20,color:'var(--text3)',fontSize:12}}>Sin medicamentos registrados</td></tr>
            )}
            {lista.map(m=>(
              <tr key={m.COD} style={{borderBottom:'1px solid var(--bg)'}}>
                <td style={{padding:'8px 12px',fontSize:12,fontWeight:600}}>{m.MEDICAMENTO}{m.NOMBRE_COMERCIAL?` (${m.NOMBRE_COMERCIAL})`:''}</td>
                <td style={{padding:'8px 12px',fontSize:12}}>{m.SUBGRUPO||'—'}</td>
                <td style={{padding:'8px 12px',fontSize:12}}>
                  {m.UNIDAD||'—'}
                  {m.UNIDADES_POR_CAJA > 1 && (
                    <div style={{fontSize:10,color:'var(--text3)'}}>1 caja = {m.UNIDADES_POR_CAJA} u</div>
                  )}
                </td>
                <td style={{padding:'8px 12px',fontSize:12,fontWeight:m.SE_PUEDE_VENDER && parseFloat(m.STOCK)<parseFloat(m.STOCK_MINIMO)?700:400,
                  color:m.SE_PUEDE_VENDER && parseFloat(m.STOCK)<parseFloat(m.STOCK_MINIMO)?'var(--red)':'var(--text)'}}>{m.SE_PUEDE_VENDER ? m.STOCK : '—'}</td>
                <td style={{padding:'8px 12px',fontSize:12}}>{m.SE_PUEDE_VENDER ? `$${Number(m.PRECIO_VENTA).toFixed(2)}` : '—'}</td>
                <td style={{padding:'8px 12px',fontSize:11}}>
                  <span style={{display:'inline-flex',alignItems:'center',padding:'3px 8px',borderRadius:20,fontSize:10,fontWeight:600,
                    background:m.SE_PUEDE_VENDER?'var(--green-lt)':'var(--amber-lt)',
                    color:m.SE_PUEDE_VENDER?'var(--green)':'var(--amber)'}}>
                    {m.SE_PUEDE_VENDER?'Vendible':'Vademécum'}
                  </span>
                  {!m.ACTIVO && <span style={{marginLeft:6,display:'inline-flex',alignItems:'center',padding:'3px 8px',borderRadius:20,fontSize:10,fontWeight:600,background:'var(--red-lt)',color:'var(--red)'}}>Inactivo</span>}
                </td>
                <td style={{padding:'8px 12px',fontSize:11,whiteSpace:'nowrap'}}>
                  <button className="btn btn-sm" onClick={()=>abrirEditar(m)}>{m.SE_PUEDE_VENDER?'Editar':'Completar'}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 16px',
          borderTop:'1px solid var(--border)',background:'var(--surface2)',fontSize:12,color:'var(--text3)',flexWrap:'wrap',gap:10}}>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <span>{total===0 ? 'Sin resultados' : `Mostrando ${iniFila}–${finFila} de ${total.toLocaleString('es-EC')}`}</span>
            <span>·</span>
            <span style={{display:'flex',alignItems:'center',gap:6}}>Filas por página:
              <select value={tam} onChange={e=>setTam(parseInt(e.target.value))}
                style={{border:'1px solid var(--border)',borderRadius:6,padding:'3px 6px',fontSize:11.5,background:'var(--surface)'}}>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </span>
          </div>
          <div style={{display:'flex',gap:4,alignItems:'center'}}>
            <button className="btn btn-sm" disabled={pagina===1} onClick={()=>setPagina(1)}>«</button>
            <button className="btn btn-sm" disabled={pagina===1} onClick={()=>setPagina(p=>p-1)}>‹</button>
            {numsPagina().map(p=>(
              <button key={p} onClick={()=>setPagina(p)}
                className="btn btn-sm"
                style={p===pagina?{background:'var(--navy)',color:'var(--surface)',borderColor:'var(--navy)',fontWeight:700}:undefined}>
                {p}
              </button>
            ))}
            <button className="btn btn-sm" disabled={pagina===totalPaginas} onClick={()=>setPagina(p=>p+1)}>›</button>
            <button className="btn btn-sm" disabled={pagina===totalPaginas} onClick={()=>setPagina(totalPaginas)}>»</button>
          </div>
        </div>
      </div>
      {showForm && (
        <div className="mb" onClick={e=>e.target===e.currentTarget&&setShowForm(false)}
          style={{position:'fixed',inset:0,background:'rgba(10,18,32,.5)',display:'flex',alignItems:'center',justifyContent:'center',padding:24,zIndex:500}}>
          <div className="mx" style={{background:'var(--surface)',borderRadius:14,width:'100%',maxWidth:640,maxHeight:'88vh',
            display:'flex',flexDirection:'column',overflow:'hidden',boxShadow:'0 24px 60px -12px rgba(10,18,32,.35)'}}>
            <div className="mh" style={{background:'var(--navy)',color:'var(--surface)',padding:'16px 22px',
              display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
              <div style={{display:'flex',alignItems:'center',gap:12}}>
                <div style={{width:38,height:38,borderRadius:10,background:'rgba(255,255,255,.14)',
                  display:'flex',alignItems:'center',justifyContent:'center',fontSize:17,flexShrink:0}}>💊</div>
                <div>
                  <div style={{fontWeight:700,fontSize:14.5}}>{editando ? 'Editar producto' : 'Nuevo producto'}</div>
                  <div style={{fontSize:11,color:'#b9c6e0',marginTop:1}}>
                    {editando ? `${form.MEDICAMENTO}${form.NOMBRE_COMERCIAL?` (${form.NOMBRE_COMERCIAL})`:''} · COD ${editando}` : 'Completa los datos y guarda'}
                  </div>
                </div>
              </div>
              <span style={{cursor:'pointer',fontSize:19,opacity:.75,width:28,height:28,display:'flex',
                alignItems:'center',justifyContent:'center',borderRadius:8}} onClick={()=>setShowForm(false)}>&#x2715;</span>
            </div>
            <div className="mbd" style={{overflowY:'auto',padding:22,flex:1}}>
              {editando && (
                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:22}}>
                  <div style={{background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:9,padding:'10px 12px',textAlign:'center'}}>
                    <div style={{fontSize:16,fontWeight:700,color:'var(--navy)'}}>{form.STOCK}</div>
                    <div style={{fontSize:9.5,color:'var(--text3)',textTransform:'uppercase',letterSpacing:.3,marginTop:2}}>Stock actual</div>
                  </div>
                  <div style={{background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:9,padding:'10px 12px',textAlign:'center'}}>
                    <div style={{fontSize:16,fontWeight:700,color:'var(--navy)'}}>${parseFloat(form.COSTO_PROMEDIO||0).toFixed(2)}</div>
                    <div style={{fontSize:9.5,color:'var(--text3)',textTransform:'uppercase',letterSpacing:.3,marginTop:2}}>Costo prom.</div>
                  </div>
                  <div style={{background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:9,padding:'10px 12px',textAlign:'center'}}>
                    <div style={{fontSize:16,fontWeight:700,color:'var(--navy)'}}>${parseFloat(form.PRECIO_VENTA||0).toFixed(2)}</div>
                    <div style={{fontSize:9.5,color:'var(--text3)',textTransform:'uppercase',letterSpacing:.3,marginTop:2}}>Precio venta</div>
                  </div>
                  <div style={{background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:9,padding:'10px 12px',textAlign:'center'}}>
                    <div style={{fontSize:16,fontWeight:700,color:'var(--navy)'}}>{parseFloat(form.UNIDADES_POR_CAJA||1)>1?`×${form.UNIDADES_POR_CAJA}`:'—'}</div>
                    <div style={{fontSize:9.5,color:'var(--text3)',textTransform:'uppercase',letterSpacing:.3,marginTop:2}}>Caja → unidad</div>
                  </div>
                </div>
              )}

              <div style={{marginBottom:22}}>
                <div style={{display:'flex',alignItems:'center',gap:8,fontSize:12,fontWeight:700,color:'var(--navy)',
                  textTransform:'uppercase',letterSpacing:.4,marginBottom:12,paddingBottom:8,borderBottom:'1px solid var(--bg)'}}>
                  <span style={{fontSize:14}}>📄</span> Información general
                </div>
                <div style={{marginBottom:10}}>
                  <label className="form-label" style={{fontSize:10}}>Nombre del medicamento *</label>
                  <input className="form-control" style={{fontSize:12}} value={form.MEDICAMENTO}
                    onChange={e=>setForm(f=>({...f,MEDICAMENTO:e.target.value}))} />
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
                  <div>
                    <label className="form-label" style={{fontSize:10}}>Nombre comercial</label>
                    <input className="form-control" style={{fontSize:12}} value={form.NOMBRE_COMERCIAL}
                      onChange={e=>setForm(f=>({...f,NOMBRE_COMERCIAL:e.target.value}))} />
                  </div>
                  <div>
                    <label className="form-label" style={{fontSize:10}}>Código de barras</label>
                    <input className="form-control" style={{fontSize:12}} value={form.COD_BARRAS}
                      onChange={e=>setForm(f=>({...f,COD_BARRAS:e.target.value}))} />
                  </div>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
                  <div>
                    <label className="form-label" style={{fontSize:10}}>Subgrupo</label>
                    <select className="form-control" style={{fontSize:12}} value={form.COD_SUBGRUPO}
                      onChange={e=>setForm(f=>({...f,COD_SUBGRUPO:e.target.value}))}>
                      <option value="">— Sin subgrupo —</option>
                      {subgrupos.map(s=>(
                        <option key={s.COD} value={s.COD}>{s.GRUPO} / {s.SUBGRUPO}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="form-label" style={{fontSize:10}}>Unidad de venta *</label>
                    <select className="form-control" style={{fontSize:12}} value={form.COD_UNIDAD}
                      onChange={e=>setForm(f=>({...f,COD_UNIDAD:e.target.value}))}>
                      <option value="">— Seleccionar —</option>
                      {unidades.map(u=>(
                        <option key={u.COD} value={u.COD}>{u.UNIDAD} — {u.DESCRIPCION}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="form-label" style={{fontSize:10}}>Código cuadro básico (opcional)</label>
                  <input className="form-control" style={{fontSize:12}} value={form.COD_CUADRO_BASICO}
                    onChange={e=>setForm(f=>({...f,COD_CUADRO_BASICO:e.target.value}))} />
                </div>
              </div>

              <div style={{marginBottom:22}}>
                <div style={{display:'flex',alignItems:'center',gap:8,fontSize:12,fontWeight:700,color:'var(--navy)',
                  textTransform:'uppercase',letterSpacing:.4,marginBottom:12,paddingBottom:8,borderBottom:'1px solid var(--bg)'}}>
                  <span style={{fontSize:14}}>📦</span> Empaque y conversión
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                  <div>
                    <label className="form-label" style={{fontSize:10}}>Unidades por caja</label>
                    <input type="number" min="1" step="1" className="form-control" style={{fontSize:12}}
                      value={form.UNIDADES_POR_CAJA} onChange={e=>setForm(f=>({...f,UNIDADES_POR_CAJA:e.target.value}))} />
                    <div style={{fontSize:10,color:'var(--text3)',marginTop:4,lineHeight:1.4}}>Se usa en Ingresos de compra para convertir cajas del proveedor a unidades.</div>
                  </div>
                  <div></div>
                </div>
              </div>

              <div style={{marginBottom:22}}>
                <div style={{display:'flex',alignItems:'center',gap:8,fontSize:12,fontWeight:700,color:'var(--navy)',
                  textTransform:'uppercase',letterSpacing:.4,marginBottom:12,paddingBottom:8,borderBottom:'1px solid var(--bg)'}}>
                  <span style={{fontSize:14}}>📊</span> Inventario
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10}}>
                  <div>
                    <label className="form-label" style={{fontSize:10}}>{editando?'Stock actual':'Stock inicial'}</label>
                    <input type="number" className="form-control" style={{fontSize:12}} value={form.STOCK}
                      disabled={!!editando}
                      onChange={e=>setForm(f=>({...f,STOCK:e.target.value}))} />
                    {editando && <div style={{fontSize:10,color:'var(--text3)',marginTop:4,lineHeight:1.4}}>Se actualiza por compras/dispensación, no editable aquí.</div>}
                  </div>
                  <div>
                    <label className="form-label" style={{fontSize:10}}>Stock mínimo</label>
                    <input type="number" className="form-control" style={{fontSize:12}} value={form.STOCK_MINIMO}
                      onChange={e=>setForm(f=>({...f,STOCK_MINIMO:e.target.value}))} />
                  </div>
                  <div></div>
                </div>
              </div>

              <div style={{marginBottom:22}}>
                <div style={{display:'flex',alignItems:'center',gap:8,fontSize:12,fontWeight:700,color:'var(--navy)',
                  textTransform:'uppercase',letterSpacing:.4,marginBottom:12,paddingBottom:8,borderBottom:'1px solid var(--bg)'}}>
                  <span style={{fontSize:14}}>💵</span> Costos y precio
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10}}>
                  <div>
                    <label className="form-label" style={{fontSize:10}}>Costo promedio</label>
                    <input type="number" className="form-control" style={{fontSize:12}} value={form.COSTO_PROMEDIO}
                      onChange={e=>setForm(f=>({...f,COSTO_PROMEDIO:e.target.value}))} />
                  </div>
                  <div>
                    <label className="form-label" style={{fontSize:10}}>% Utilidad</label>
                    <input type="number" className="form-control" style={{fontSize:12}} value={form.PORCENTAJE_UTILIDAD}
                      onChange={e=>setForm(f=>({...f,PORCENTAJE_UTILIDAD:e.target.value}))} />
                  </div>
                  <div>
                    <label className="form-label" style={{fontSize:10}}>Precio de venta</label>
                    <input type="number" className="form-control" style={{fontSize:12}} value={form.PRECIO_VENTA}
                      onChange={e=>setForm(f=>({...f,PRECIO_VENTA:e.target.value}))} />
                  </div>
                </div>
              </div>

              <div>
                <div style={{display:'flex',alignItems:'center',gap:8,fontSize:12,fontWeight:700,color:'var(--navy)',
                  textTransform:'uppercase',letterSpacing:.4,marginBottom:12,paddingBottom:8,borderBottom:'1px solid var(--bg)'}}>
                  <span style={{fontSize:14}}>🔘</span> Estado
                </div>
                <div style={{display:'grid',gridTemplateColumns:editando?'1fr 1fr':'1fr',gap:10}}>
                  <label style={{display:'flex',alignItems:'center',gap:8,fontSize:12.5,color:'var(--text)',
                    background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:8,padding:'9px 12px'}}>
                    <input type="checkbox" style={{width:15,height:15}} checked={!!form.SE_PUEDE_VENDER}
                      onChange={e=>setForm(f=>({...f,SE_PUEDE_VENDER:e.target.checked?1:0}))} />
                    Se puede vender
                  </label>
                  {editando && (
                    <label style={{display:'flex',alignItems:'center',gap:8,fontSize:12.5,color:'var(--text)',
                      background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:8,padding:'9px 12px'}}>
                      <input type="checkbox" style={{width:15,height:15}} checked={!!form.ACTIVO}
                        onChange={e=>setForm(f=>({...f,ACTIVO:e.target.checked?1:0}))} />
                      Activo
                    </label>
                  )}
                </div>
              </div>
            </div>
            <div style={{padding:'14px 22px',borderTop:'1px solid var(--border)',display:'flex',justifyContent:'space-between',
              alignItems:'center',flexShrink:0,background:'var(--surface2)'}}>
              <div style={{fontSize:11,color:'var(--text3)'}}>
                {err ? <span style={{color:'var(--red)'}}>{err}</span> : '* Campos obligatorios'}
              </div>
              <div style={{display:'flex',gap:8}}>
                <button className="btn btn-sm" onClick={()=>setShowForm(false)}>Cancelar</button>
                <button className="btn btn-primary btn-sm" disabled={saving} onClick={guardar}>
                  {saving ? 'Guardando...' : 'Guardar cambios'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showAdmin && (
        <AdminSubgruposUnidades
          onClose={() => setShowAdmin(false)}
          onChange={() => {
            api.get('/farmacia/subgrupos').then(({data}) => setSubgrupos(data||[])).catch(()=>{});
            api.get('/farmacia/unidades').then(({data}) => setUnidades(data||[])).catch(()=>{});
          }}
        />
      )}
    </div>
  );
}
/* ── Mini administrador de Subgrupos y Unidades (usado por PanelCatalogo) ── */
function AdminSubgruposUnidades({ onClose, onChange }) {
  const VACIO_SG = { SUBGRUPO:'', COD_GRUPO:'', ACTIVO:1 };
  const VACIO_UN = { UNIDAD:'', DESCRIPCION:'' };
  const [grupos, setGrupos] = useState([]);
  const [subgrupos, setSubgrupos] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [formSG, setFormSG] = useState(VACIO_SG);
  const [editandoSG, setEditandoSG] = useState(null);
  const [savingSG, setSavingSG] = useState(false);
  const [errSG, setErrSG] = useState('');
  const [formUN, setFormUN] = useState(VACIO_UN);
  const [editandoUN, setEditandoUN] = useState(null);
  const [savingUN, setSavingUN] = useState(false);
  const [errUN, setErrUN] = useState('');

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [g, s, u] = await Promise.all([
        api.get('/farmacia/grupos'),
        api.get('/farmacia/subgrupos?activo=todos'),
        api.get('/farmacia/unidades'),
      ]);
      setGrupos(g.data || []);
      setSubgrupos(s.data || []);
      setUnidades(u.data || []);
    } catch (e) { console.error(e); }
    setCargando(false);
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const abrirEditarSG = (s) => {
    setEditandoSG(s.COD);
    setFormSG({ SUBGRUPO:s.SUBGRUPO||'', COD_GRUPO:s.COD_GRUPO||'', ACTIVO:s.ACTIVO??1 });
    setErrSG('');
  };
  const cancelarSG = () => { setEditandoSG(null); setFormSG(VACIO_SG); setErrSG(''); };
  const guardarSG = async () => {
    if (!formSG.SUBGRUPO.trim()) { setErrSG('Nombre requerido'); return; }
    if (!formSG.COD_GRUPO) { setErrSG('Grupo requerido'); return; }
    setSavingSG(true); setErrSG('');
    try {
      if (editandoSG) await api.put(`/farmacia/subgrupos/${editandoSG}`, formSG);
      else await api.post('/farmacia/subgrupos', formSG);
      cancelarSG();
      cargar(); onChange();
    } catch (e) { setErrSG(e.response?.data?.error || e.message); }
    setSavingSG(false);
  };
  const toggleActivoSG = async (s) => {
    try {
      await api.put(`/farmacia/subgrupos/${s.COD}`, { SUBGRUPO:s.SUBGRUPO, COD_GRUPO:s.COD_GRUPO, ACTIVO:s.ACTIVO?0:1 });
      cargar(); onChange();
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const abrirEditarUN = (u) => {
    setEditandoUN(u.COD);
    setFormUN({ UNIDAD:u.UNIDAD||'', DESCRIPCION:u.DESCRIPCION||'' });
    setErrUN('');
  };
  const cancelarUN = () => { setEditandoUN(null); setFormUN(VACIO_UN); setErrUN(''); };
  const guardarUN = async () => {
    if (!formUN.UNIDAD.trim()) { setErrUN('Nombre requerido'); return; }
    setSavingUN(true); setErrUN('');
    try {
      if (editandoUN) await api.put(`/farmacia/unidades/${editandoUN}`, formUN);
      else await api.post('/farmacia/unidades', formUN);
      cancelarUN();
      cargar(); onChange();
    } catch (e) { setErrUN(e.response?.data?.error || e.message); }
    setSavingUN(false);
  };

  return (
    <div onClick={e=>e.target===e.currentTarget&&onClose()}
      style={{position:'fixed',inset:0,background:'rgba(10,18,32,.5)',display:'flex',alignItems:'center',justifyContent:'center',padding:24,zIndex:500}}>
      <div style={{background:'var(--surface)',borderRadius:14,width:'100%',maxWidth:640,maxHeight:'88vh',
        display:'flex',flexDirection:'column',overflow:'hidden',boxShadow:'0 24px 60px -12px rgba(10,18,32,.35)'}}>
        <div style={{background:'var(--navy)',color:'var(--surface)',padding:'16px 22px',
          display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
          <span style={{fontWeight:700,fontSize:13}}>Administrar subgrupos y unidades</span>
          <span style={{cursor:'pointer',fontSize:18,opacity:.8}} onClick={onClose}>&#x2715;</span>
        </div>
        <div style={{overflowY:'auto',padding:22,flex:1}}>
          {cargando && <div style={{textAlign:'center',padding:20,color:'var(--text3)',fontSize:12}}>Cargando...</div>}
          {!cargando && (
            <>
              <div style={{marginBottom:22}}>
                <div style={{fontWeight:700,fontSize:12.5,color:'var(--navy)',marginBottom:8}}>Subgrupos</div>
                <div style={{display:'flex',gap:8,marginBottom:8,flexWrap:'wrap'}}>
                  <input className="form-control" style={{fontSize:12,maxWidth:200}} placeholder="Nombre del subgrupo"
                    value={formSG.SUBGRUPO} onChange={e=>setFormSG(f=>({...f,SUBGRUPO:e.target.value}))} />
                  <select className="form-control" style={{fontSize:12,maxWidth:180}} value={formSG.COD_GRUPO}
                    onChange={e=>setFormSG(f=>({...f,COD_GRUPO:e.target.value}))}>
                    <option value="">— Grupo —</option>
                    {grupos.map(g=>(<option key={g.COD} value={g.COD}>{g.GRUPO}</option>))}
                  </select>
                  {editandoSG && (
                    <label style={{display:'flex',alignItems:'center',gap:4,fontSize:12}}>
                      <input type="checkbox" checked={!!formSG.ACTIVO}
                        onChange={e=>setFormSG(f=>({...f,ACTIVO:e.target.checked?1:0}))} />
                      Activo
                    </label>
                  )}
                  <button className="btn btn-primary btn-sm" disabled={savingSG} onClick={guardarSG}>
                    {editandoSG ? (savingSG?'Guardando...':'Guardar cambios') : (savingSG?'Agregando...':'+ Agregar')}
                  </button>
                  {editandoSG && <button className="btn btn-sm" onClick={cancelarSG}>Cancelar</button>}
                </div>
                {errSG && <div style={{color:'var(--red)',fontSize:11,marginBottom:8}}>{errSG}</div>}
                <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,overflow:'hidden',maxHeight:220,overflowY:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse'}}>
                    <thead>
                      <tr style={{background:'var(--blue-lt)'}}>
                        {['Subgrupo','Grupo','Estado',''].map(h=>(
                          <th key={h} style={{padding:'6px 10px',fontSize:10,fontWeight:700,textTransform:'uppercase',
                            letterSpacing:'.4px',color:'var(--navy)',textAlign:'left',borderBottom:'1px solid var(--border)'}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {subgrupos.length===0 && (
                        <tr><td colSpan={4} style={{textAlign:'center',padding:14,color:'var(--text3)',fontSize:12}}>Sin subgrupos</td></tr>
                      )}
                      {subgrupos.map(s=>(
                        <tr key={s.COD} style={{borderBottom:'1px solid var(--bg)'}}>
                          <td style={{padding:'6px 10px',fontSize:12}}>{s.SUBGRUPO}</td>
                          <td style={{padding:'6px 10px',fontSize:12}}>{s.GRUPO}</td>
                          <td style={{padding:'6px 10px',fontSize:11}}>
                            <span style={{display:'inline-flex',alignItems:'center',padding:'3px 8px',borderRadius:20,fontSize:10,fontWeight:600,
                              background:s.ACTIVO?'var(--green-lt)':'var(--red-lt)',color:s.ACTIVO?'var(--green)':'var(--red)'}}>
                              {s.ACTIVO?'Activo':'Inactivo'}
                            </span>
                          </td>
                          <td style={{padding:'6px 10px',fontSize:11,whiteSpace:'nowrap'}}>
                            <button className="btn btn-sm" style={{marginRight:6}} onClick={()=>abrirEditarSG(s)}>Editar</button>
                            <button className="btn btn-sm" onClick={()=>toggleActivoSG(s)}>{s.ACTIVO?'Desactivar':'Activar'}</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <div style={{fontWeight:700,fontSize:12.5,color:'var(--navy)',marginBottom:8}}>Unidades de farmacia</div>
                <div style={{display:'flex',gap:8,marginBottom:8,flexWrap:'wrap'}}>
                  <input className="form-control" style={{fontSize:12,maxWidth:140}} placeholder="Código (ej. TAB)"
                    value={formUN.UNIDAD} onChange={e=>setFormUN(f=>({...f,UNIDAD:e.target.value}))} />
                  <input className="form-control" style={{fontSize:12,maxWidth:220}} placeholder="Descripción (ej. Tableta)"
                    value={formUN.DESCRIPCION} onChange={e=>setFormUN(f=>({...f,DESCRIPCION:e.target.value}))} />
                  <button className="btn btn-primary btn-sm" disabled={savingUN} onClick={guardarUN}>
                    {editandoUN ? (savingUN?'Guardando...':'Guardar cambios') : (savingUN?'Agregando...':'+ Agregar')}
                  </button>
                  {editandoUN && <button className="btn btn-sm" onClick={cancelarUN}>Cancelar</button>}
                </div>
                {errUN && <div style={{color:'var(--red)',fontSize:11,marginBottom:8}}>{errUN}</div>}
                <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,overflow:'hidden',maxHeight:220,overflowY:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse'}}>
                    <thead>
                      <tr style={{background:'var(--blue-lt)'}}>
                        {['Código','Descripción',''].map(h=>(
                          <th key={h} style={{padding:'6px 10px',fontSize:10,fontWeight:700,textTransform:'uppercase',
                            letterSpacing:'.4px',color:'var(--navy)',textAlign:'left',borderBottom:'1px solid var(--border)'}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {unidades.length===0 && (
                        <tr><td colSpan={3} style={{textAlign:'center',padding:14,color:'var(--text3)',fontSize:12}}>Sin unidades</td></tr>
                      )}
                      {unidades.map(u=>(
                        <tr key={u.COD} style={{borderBottom:'1px solid var(--bg)'}}>
                          <td style={{padding:'6px 10px',fontSize:12,fontWeight:600}}>{u.UNIDAD}</td>
                          <td style={{padding:'6px 10px',fontSize:12}}>{u.DESCRIPCION||'—'}</td>
                          <td style={{padding:'6px 10px',fontSize:11,whiteSpace:'nowrap'}}>
                            <button className="btn btn-sm" onClick={()=>abrirEditarUN(u)}>Editar</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PanelProveedores() {
  const VACIO = { RAZON_SOCIAL:'', IDENTIFICACION:'', CIUDAD:'', DIRECCION:'', TELEFONOS:'', EMAIL:'' };
  const [lista, setLista]   = useState([]);
  const [resumen, setResumen] = useState({ compras:{}, overrides:{} });
  const [busq, setBusq]     = useState('');
  const [pagina, setPagina] = useState(1);
  const [tam, setTam]       = useState(20);
  const [cargando, setCargando] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editando, setEditando] = useState(null); // COD si se está editando, null si es nuevo
  const [form, setForm]     = useState(VACIO);
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState('');
  const [verConversiones, setVerConversiones] = useState(null); // proveedor seleccionado, o null

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [rProv, rResumen] = await Promise.all([
        api.get(`/proveedores${busq ? `?q=${encodeURIComponent(busq)}` : ''}`),
        api.get('/farmacia/proveedores-resumen').catch(() => ({ data: { compras:{}, overrides:{} } })),
      ]);
      setLista(rProv.data || []);
      setResumen(rResumen.data || { compras:{}, overrides:{} });
    } catch (e) { console.error(e); }
    setCargando(false);
  }, [busq]);

  useEffect(() => {
    const t = setTimeout(cargar, 300);
    return () => clearTimeout(t);
  }, [cargar]);
  useEffect(() => { setPagina(1); }, [busq, tam]);

  const total = lista.length;
  const totalPaginas = Math.max(1, Math.ceil(total / tam));
  const paginaActual = Math.min(pagina, totalPaginas);
  const iniFila = total === 0 ? 0 : (paginaActual - 1) * tam + 1;
  const finFila = Math.min(paginaActual * tam, total);
  const listaPagina = lista.slice((paginaActual - 1) * tam, finFila);
  const numsPagina = () => {
    const start = Math.max(1, paginaActual - 2), end = Math.min(totalPaginas, paginaActual + 2);
    const nums = [];
    for (let p = start; p <= end; p++) nums.push(p);
    return nums;
  };

  const abrirNuevo = () => { setEditando(null); setForm(VACIO); setErr(''); setShowForm(true); };
  const abrirEditar = (p) => {
    setEditando(p.COD);
    setForm({ RAZON_SOCIAL:p.RAZON_SOCIAL||'', IDENTIFICACION:p.IDENTIFICACION||'', CIUDAD:p.CIUDAD||'',
               DIRECCION:p.DIRECCION||'', TELEFONOS:p.TELEFONOS||'', EMAIL:p.EMAIL||'' });
    setErr(''); setShowForm(true);
  };
  const guardar = async () => {
    if (!form.RAZON_SOCIAL.trim() || !form.IDENTIFICACION.trim()) {
      setErr('Razón social e identificación son obligatorias'); return;
    }
    setSaving(true); setErr('');
    try {
      if (editando) await api.put(`/proveedores/${editando}`, form);
      else await api.post('/proveedores', form);
      setShowForm(false);
      cargar();
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    setSaving(false);
  };
  const desactivar = async (p) => {
    if (!window.confirm(`¿Desactivar a "${p.RAZON_SOCIAL}"?`)) return;
    try { await api.delete(`/proveedores/${p.COD}`); cargar(); }
    catch (e) { alert(e.response?.data?.error || e.message); }
  };

  return (
    <div>
      <div style={{display:'flex',gap:10,marginBottom:14,alignItems:'center'}}>
        <input className="form-control" style={{fontSize:12,maxWidth:280}} placeholder="Buscar proveedor..."
          value={busq} onChange={e=>setBusq(e.target.value)} />
        <button className="btn btn-primary btn-sm" onClick={abrirNuevo}>+ Nuevo proveedor</button>
      </div>
      <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,overflow:'hidden'}}>
        <table style={{width:'100%',borderCollapse:'collapse'}}>
          <thead>
            <tr style={{background:'var(--blue-lt)'}}>
              {['Proveedor','RUC','Contacto','Conversión caja→unidad','Últimas compras',''].map(h=>(
                <th key={h} style={{padding:'8px 12px',fontSize:10,fontWeight:700,textTransform:'uppercase',
                  letterSpacing:'.4px',color:'var(--navy)',textAlign:'left',borderBottom:'1px solid var(--border)'}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cargando && <tr><td colSpan={6} style={{textAlign:'center',padding:20,color:'var(--text3)',fontSize:12}}>Cargando...</td></tr>}
            {!cargando && lista.length===0 && (
              <tr><td colSpan={6} style={{textAlign:'center',padding:20,color:'var(--text3)',fontSize:12}}>Sin proveedores registrados</td></tr>
            )}
            {listaPagina.map(p=>{
              const nOverrides = resumen.overrides?.[p.COD] || 0;
              const nCompras = resumen.compras?.[p.COD] || 0;
              return (
              <tr key={p.COD} style={{borderBottom:'1px solid var(--bg)'}}>
                <td style={{padding:'8px 12px',fontSize:12,fontWeight:600}}>
                  {p.RAZON_SOCIAL}
                  {p.C_ESTADO!=='ACT' && <span style={{marginLeft:6,display:'inline-flex',alignItems:'center',padding:'2px 7px',borderRadius:20,fontSize:9.5,fontWeight:600,background:'var(--red-lt)',color:'var(--red)'}}>Inactivo</span>}
                </td>
                <td style={{padding:'8px 12px',fontSize:12}}>{p.IDENTIFICACION}</td>
                <td style={{padding:'8px 12px',fontSize:11.5,color:'var(--text2)'}}>
                  {p.EMAIL||'—'}
                  {p.TELEFONOS && <div style={{fontSize:10,color:'var(--text3)',marginTop:1}}>{p.TELEFONOS}</div>}
                </td>
                <td style={{padding:'8px 12px',fontSize:11}}>
                  <span style={{display:'inline-flex',alignItems:'center',padding:'3px 8px',borderRadius:20,fontSize:10,fontWeight:600,
                    background:nOverrides>0?'var(--amber-lt)':'var(--blue-lt)',
                    color:nOverrides>0?'var(--amber)':'var(--blue-mid)'}}>
                    {nOverrides>0 ? `Override en ${nOverrides} producto${nOverrides!==1?'s':''}` : 'Usa factor del catálogo'}
                  </span>
                </td>
                <td style={{padding:'8px 12px',fontSize:12}}>{nCompras}</td>
                <td style={{padding:'8px 12px',fontSize:11,whiteSpace:'nowrap'}}>
                  <button className="btn btn-sm" style={{marginRight:6}} onClick={()=>abrirEditar(p)}>Editar</button>
                  <button className="btn btn-sm" style={{marginRight:6}} onClick={()=>setVerConversiones(p)}>🔁 Conversiones</button>
                  {p.C_ESTADO==='ACT' && <button className="btn btn-sm" style={{color:'var(--red)',borderColor:'var(--red)'}} onClick={()=>desactivar(p)}>Desactivar</button>}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 16px',
          borderTop:'1px solid var(--border)',background:'var(--surface2)',fontSize:12,color:'var(--text3)',flexWrap:'wrap',gap:10}}>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <span>{total===0 ? 'Sin resultados' : `Mostrando ${iniFila}–${finFila} de ${total.toLocaleString('es-EC')}`}</span>
            <span>·</span>
            <span style={{display:'flex',alignItems:'center',gap:6}}>Filas por página:
              <select value={tam} onChange={e=>setTam(parseInt(e.target.value))}
                style={{border:'1px solid var(--border)',borderRadius:6,padding:'3px 6px',fontSize:11.5,background:'var(--surface)'}}>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </span>
          </div>
          <div style={{display:'flex',gap:4,alignItems:'center'}}>
            <button className="btn btn-sm" disabled={paginaActual===1} onClick={()=>setPagina(1)}>«</button>
            <button className="btn btn-sm" disabled={paginaActual===1} onClick={()=>setPagina(p=>p-1)}>‹</button>
            {numsPagina().map(p=>(
              <button key={p} onClick={()=>setPagina(p)}
                className="btn btn-sm"
                style={p===paginaActual?{background:'var(--navy)',color:'var(--surface)',borderColor:'var(--navy)',fontWeight:700}:undefined}>
                {p}
              </button>
            ))}
            <button className="btn btn-sm" disabled={paginaActual===totalPaginas} onClick={()=>setPagina(p=>p+1)}>›</button>
            <button className="btn btn-sm" disabled={paginaActual===totalPaginas} onClick={()=>setPagina(totalPaginas)}>»</button>
          </div>
        </div>
      </div>
      {showForm && (
        <div onClick={e=>e.target===e.currentTarget&&setShowForm(false)}
          style={{position:'fixed',inset:0,background:'rgba(10,18,32,.5)',display:'flex',alignItems:'center',justifyContent:'center',padding:24,zIndex:500}}>
          <div style={{background:'var(--surface)',borderRadius:14,width:'100%',maxWidth:520,maxHeight:'88vh',
            display:'flex',flexDirection:'column',overflow:'hidden',boxShadow:'0 24px 60px -12px rgba(10,18,32,.35)'}}>
            <div style={{background:'var(--navy)',color:'var(--surface)',padding:'16px 22px',
              display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
              <div style={{display:'flex',alignItems:'center',gap:12}}>
                <div style={{width:38,height:38,borderRadius:10,background:'rgba(255,255,255,.14)',
                  display:'flex',alignItems:'center',justifyContent:'center',fontSize:17,flexShrink:0}}>🚚</div>
                <div>
                  <div style={{fontWeight:700,fontSize:14.5}}>{editando ? 'Editar proveedor' : 'Nuevo proveedor'}</div>
                  <div style={{fontSize:11,color:'#b9c6e0',marginTop:1}}>
                    {editando ? (form.RAZON_SOCIAL || `COD ${editando}`) : 'Completa los datos y guarda'}
                  </div>
                </div>
              </div>
              <span style={{cursor:'pointer',fontSize:19,opacity:.75,width:28,height:28,display:'flex',
                alignItems:'center',justifyContent:'center',borderRadius:8}} onClick={()=>setShowForm(false)}>&#x2715;</span>
            </div>
            <div style={{overflowY:'auto',padding:22,flex:1}}>
              <div style={{marginBottom:22}}>
                <div style={{display:'flex',alignItems:'center',gap:8,fontSize:12,fontWeight:700,color:'var(--navy)',
                  textTransform:'uppercase',letterSpacing:.4,marginBottom:12,paddingBottom:8,borderBottom:'1px solid var(--bg)'}}>
                  <span style={{fontSize:14}}>🏢</span> Información general
                </div>
                <div style={{marginBottom:10}}>
                  <label className="form-label" style={{fontSize:10}}>Razón social *</label>
                  <input className="form-control" style={{fontSize:12}} value={form.RAZON_SOCIAL}
                    onChange={e=>setForm(f=>({...f,RAZON_SOCIAL:e.target.value}))} />
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                  <div>
                    <label className="form-label" style={{fontSize:10}}>Identificación (RUC/Cédula) *</label>
                    <input className="form-control" style={{fontSize:12}} value={form.IDENTIFICACION}
                      onChange={e=>setForm(f=>({...f,IDENTIFICACION:e.target.value}))} />
                  </div>
                  <div>
                    <label className="form-label" style={{fontSize:10}}>Ciudad</label>
                    <input className="form-control" style={{fontSize:12}} value={form.CIUDAD}
                      onChange={e=>setForm(f=>({...f,CIUDAD:e.target.value}))} />
                  </div>
                </div>
              </div>
              <div>
                <div style={{display:'flex',alignItems:'center',gap:8,fontSize:12,fontWeight:700,color:'var(--navy)',
                  textTransform:'uppercase',letterSpacing:.4,marginBottom:12,paddingBottom:8,borderBottom:'1px solid var(--bg)'}}>
                  <span style={{fontSize:14}}>📇</span> Contacto
                </div>
                <div style={{marginBottom:10}}>
                  <label className="form-label" style={{fontSize:10}}>Dirección</label>
                  <input className="form-control" style={{fontSize:12}} value={form.DIRECCION}
                    onChange={e=>setForm(f=>({...f,DIRECCION:e.target.value}))} />
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                  <div>
                    <label className="form-label" style={{fontSize:10}}>Teléfono</label>
                    <input className="form-control" style={{fontSize:12}} value={form.TELEFONOS}
                      onChange={e=>setForm(f=>({...f,TELEFONOS:e.target.value}))} />
                  </div>
                  <div>
                    <label className="form-label" style={{fontSize:10}}>Email</label>
                    <input className="form-control" style={{fontSize:12}} value={form.EMAIL}
                      onChange={e=>setForm(f=>({...f,EMAIL:e.target.value}))} />
                  </div>
                </div>
              </div>
            </div>
            <div style={{padding:'14px 22px',borderTop:'1px solid var(--border)',display:'flex',justifyContent:'space-between',
              alignItems:'center',flexShrink:0,background:'var(--surface2)'}}>
              <div style={{fontSize:11,color:'var(--text3)'}}>
                {err ? <span style={{color:'var(--red)'}}>{err}</span> : '* Campos obligatorios'}
              </div>
              <div style={{display:'flex',gap:8}}>
                <button className="btn btn-sm" onClick={()=>setShowForm(false)}>Cancelar</button>
                <button className="btn btn-primary btn-sm" disabled={saving} onClick={guardar}>
                  {saving ? 'Guardando...' : 'Guardar cambios'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {verConversiones && (
        <ConversionesProveedor proveedor={verConversiones} onClose={()=>setVerConversiones(null)} />
      )}
    </div>
  );
}

/* ── Gestor de conversión caja→unidad por proveedor (usado por PanelProveedores) ── */
function ConversionesProveedor({ proveedor, onClose }) {
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [busqMed, setBusqMed] = useState('');
  const [sugsMed, setSugsMed] = useState([]);
  const [medSel, setMedSel] = useState(null);
  const [factor, setFactor] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const cargar = useCallback(async () => {
    setCargando(true);
    try { const { data } = await api.get(`/farmacia/proveedores/${proveedor.COD}/conversiones`); setLista(data||[]); }
    catch (e) { console.error(e); }
    setCargando(false);
  }, [proveedor.COD]);
  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    if (busqMed.length < 2) { setSugsMed([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/historia/medicamentos?q=${encodeURIComponent(busqMed)}`); setSugsMed(data||[]); } catch { setSugsMed([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [busqMed]);

  const guardar = async () => {
    if (!medSel) { setErr('Selecciona un medicamento'); return; }
    if (!factor || parseInt(factor) < 1) { setErr('Unidades por caja debe ser 1 o más'); return; }
    setSaving(true); setErr('');
    try {
      await api.post(`/farmacia/proveedores/${proveedor.COD}/conversiones`, { COD_MEDICAMENTO: medSel.COD, UNIDADES_POR_CAJA: factor });
      setMedSel(null); setBusqMed(''); setFactor('');
      cargar();
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    setSaving(false);
  };
  const quitar = async (codMedicamento) => {
    try { await api.delete(`/farmacia/proveedores/${proveedor.COD}/conversiones/${codMedicamento}`); cargar(); }
    catch (e) { alert(e.response?.data?.error || e.message); }
  };

  return (
    <div onClick={e=>e.target===e.currentTarget&&onClose()}
      style={{position:'fixed',inset:0,background:'rgba(10,18,32,.5)',display:'flex',alignItems:'center',justifyContent:'center',padding:24,zIndex:500}}>
      <div style={{background:'var(--surface)',borderRadius:14,width:'100%',maxWidth:560,maxHeight:'88vh',
        display:'flex',flexDirection:'column',overflow:'hidden',boxShadow:'0 24px 60px -12px rgba(10,18,32,.35)'}}>
        <div style={{background:'var(--navy)',color:'var(--surface)',padding:'16px 22px',
          display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
          <span style={{fontWeight:700,fontSize:13}}>Conversión caja→unidad — {proveedor.RAZON_SOCIAL}</span>
          <span style={{cursor:'pointer',fontSize:18,opacity:.8}} onClick={onClose}>&#x2715;</span>
        </div>
        <div style={{overflowY:'auto',padding:22,flex:1}}>
          <div style={{fontSize:11,color:'var(--text3)',marginBottom:12}}>
            Solo agrega un override aquí si este proveedor empaca distinto al factor definido en el Catálogo para ese producto. Si no hay override, se usa siempre el del Catálogo.
          </div>
          <div style={{display:'flex',gap:8,marginBottom:12,alignItems:'flex-start',position:'relative',flexWrap:'wrap'}}>
            <div style={{flex:1,minWidth:200,position:'relative'}}>
              {medSel ? (
                <div style={{display:'flex',alignItems:'center',gap:6,background:'var(--blue-lt)',borderRadius:5,padding:'6px 10px',fontSize:12}}>
                  <span style={{flex:1,fontWeight:600}}>{medSel.MEDICAMENTO}</span>
                  <span style={{cursor:'pointer'}} onClick={()=>setMedSel(null)}>&#x2715;</span>
                </div>
              ) : (
                <>
                  <input className="form-control" style={{fontSize:12}} placeholder="Buscar medicamento..."
                    value={busqMed} onChange={e=>setBusqMed(e.target.value)} />
                  {sugsMed.length>0 && (
                    <div style={{position:'absolute',top:'100%',left:0,right:0,background:'var(--surface)',border:'1px solid var(--border)',
                      borderRadius:5,zIndex:10,maxHeight:160,overflowY:'auto',boxShadow:'0 4px 12px rgba(0,0,0,.1)'}}>
                      {sugsMed.map(m=>(
                        <div key={m.COD} onClick={()=>{setMedSel(m);setBusqMed('');setSugsMed([]);}} style={{padding:'6px 10px',fontSize:12,cursor:'pointer',borderBottom:'1px solid var(--surface2)'}}
                          onMouseEnter={e=>e.currentTarget.style.background='var(--blue-lt)'}
                          onMouseLeave={e=>e.currentTarget.style.background=''}>
                          {m.MEDICAMENTO}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            <input type="number" min="1" step="1" className="form-control" style={{fontSize:12,width:110}}
              placeholder="Unidades/caja" value={factor} onChange={e=>setFactor(e.target.value)} />
            <button className="btn btn-primary btn-sm" disabled={saving} onClick={guardar}>
              {saving?'Guardando...':'+ Agregar'}
            </button>
          </div>
          {err && <div style={{color:'var(--red)',fontSize:11,marginBottom:10}}>{err}</div>}
          <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,overflow:'hidden'}}>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead>
                <tr style={{background:'var(--blue-lt)'}}>
                  {['Medicamento','Catálogo','Override este proveedor',''].map(h=>(
                    <th key={h} style={{padding:'8px 12px',fontSize:10,fontWeight:700,textTransform:'uppercase',
                      letterSpacing:'.4px',color:'var(--navy)',textAlign:'left',borderBottom:'1px solid var(--border)'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cargando && <tr><td colSpan={4} style={{textAlign:'center',padding:20,color:'var(--text3)',fontSize:12}}>Cargando...</td></tr>}
                {!cargando && lista.length===0 && (
                  <tr><td colSpan={4} style={{textAlign:'center',padding:20,color:'var(--text3)',fontSize:12}}>Sin overrides — todos los productos usan el factor del Catálogo</td></tr>
                )}
                {lista.map(c=>(
                  <tr key={c.COD} style={{borderBottom:'1px solid var(--bg)'}}>
                    <td style={{padding:'8px 12px',fontSize:12,fontWeight:600}}>{c.MEDICAMENTO}</td>
                    <td style={{padding:'8px 12px',fontSize:12,color:'var(--text3)'}}>1 caja = {c.UNIDADES_POR_CAJA_CATALOGO} u</td>
                    <td style={{padding:'8px 12px',fontSize:12,fontWeight:600,color:'var(--blue-mid)'}}>1 caja = {c.UNIDADES_POR_CAJA} u</td>
                    <td style={{padding:'8px 12px',fontSize:11}}>
                      <span style={{cursor:'pointer',color:'var(--red)'}} onClick={()=>quitar(c.COD_MEDICAMENTO)}>Quitar</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{display:'flex',justifyContent:'flex-end',marginTop:12}}>
            <button className="btn btn-sm" onClick={onClose}>Cerrar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Panel de dispensación ── */
function PanelDispensacion({ codRecetaInicial, codPacienteInicial }) {
  const navigate = useNavigate();
  const [cola, setCola]               = useState([]);
  const [cargandoCola, setCargandoCola] = useState(true);
  const [busqueda, setBusqueda]       = useState('');
  const [soloUrgentes, setSoloUrgentes] = useState(false);
  const [filtroMedico, setFiltroMedico] = useState('');
  const [ordenCola, setOrdenCola]     = useState('antiguo'); // 'antiguo' = espera mayor primero, 'reciente' = espera menor primero
  const [paginaCola, setPaginaCola]   = useState(1);
  const TAM_COLA = 8;
  const [codReceta, setCodReceta]     = useState(codRecetaInicial || '');
  const [codPaciente, setCodPaciente] = useState(codPacienteInicial || '');
  const [pacienteNombre, setPacienteNombre] = useState('');
  const [items, setItems]             = useState([]);
  const [sel, setSel]                 = useState([]); // COD_DETALLE_RECETA seleccionados
  const [cantidades, setCantidades]   = useState({}); // COD_DETALLE_RECETA -> cantidad a dispensar ahora
  const [historial, setHistorial]     = useState([]);
  const [anulando, setAnulando]       = useState(null); // COD_CABECERA en proceso
  const [cargando, setCargando]       = useState(false);
  const [enviando, setEnviando]       = useState(false);
  const [msg, setMsg]                 = useState('');
  // ── Venta libre / receta externa (paciente llega con receta de otro médico o centro) ──
  const [modoLibre, setModoLibre]           = useState(false);
  const [busqPacLibre, setBusqPacLibre]     = useState('');
  const [sugsPacLibre, setSugsPacLibre]     = useState([]);
  const [codPacLibre, setCodPacLibre]       = useState('');
  const [pacNombreLibre, setPacNombreLibre] = useState('');
  const [busqMedLibre, setBusqMedLibre]     = useState('');
  const [sugsMedLibre, setSugsMedLibre]     = useState([]);
  const [carritoLibre, setCarritoLibre]     = useState([]); // {COD, MEDICAMENTO, CANTIDAD, PRECIO_VENTA, STOCK}
  const [notaLibre, setNotaLibre]           = useState('');
  const [enviandoLibre, setEnviandoLibre]   = useState(false);
  const [msgLibre, setMsgLibre]             = useState('');
  // ── Registrar paciente nuevo (no encontrado en la búsqueda de Venta libre) ──
  const NUEVO_PAC_VACIO = { C_TIPO_IDENTIFICACION:'CDL', IDENTIFICACION:'', APELLIDO_PATERNO:'', APELLIDO_MATERNO:'', PRIMER_NOMBRE:'', SEGUNDO_NOMBRE:'', C_SEXO:'', TELEFONO:'', EMAIL:'', DIRECCION_DOMICILIO:'' };
  const [showNuevoPacLibre, setShowNuevoPacLibre]           = useState(false);
  const [nuevoPacLibre, setNuevoPacLibre]                   = useState(NUEVO_PAC_VACIO);
  const [buscandoCedulaPacLibre, setBuscandoCedulaPacLibre] = useState(false);
  const [msgCedulaPacLibre, setMsgCedulaPacLibre]           = useState('');
  const [guardandoPacLibre, setGuardandoPacLibre]           = useState(false);
  const [errPacLibre, setErrPacLibre]                       = useState('');
  const abrirNuevoPacLibre = () => {
    const cedulaPrellenada = /^\d{10}$/.test(busqPacLibre.trim()) ? busqPacLibre.trim() : '';
    setNuevoPacLibre({ ...NUEVO_PAC_VACIO, IDENTIFICACION: cedulaPrellenada });
    setErrPacLibre(''); setMsgCedulaPacLibre(''); setShowNuevoPacLibre(true);
  };
  const buscarCedulaPacLibre = async () => {
    const cedula = (nuevoPacLibre.IDENTIFICACION || '').trim();
    if (!/^\d{10}$/.test(cedula)) { setMsgCedulaPacLibre('Ingresa una cédula válida de 10 dígitos'); return; }
    setBuscandoCedulaPacLibre(true); setMsgCedulaPacLibre('');
    try {
      const { data } = await api.get(`/pacientes/consultar-cedula/${cedula}`);
      setNuevoPacLibre((p) => ({
        ...p,
        APELLIDO_PATERNO: data.APELLIDO_PATERNO || p.APELLIDO_PATERNO,
        APELLIDO_MATERNO: data.APELLIDO_MATERNO || p.APELLIDO_MATERNO,
        PRIMER_NOMBRE: data.PRIMER_NOMBRE || p.PRIMER_NOMBRE,
        SEGUNDO_NOMBRE: data.SEGUNDO_NOMBRE || p.SEGUNDO_NOMBRE,
        C_SEXO: data.C_SEXO || p.C_SEXO,
      }));
      setMsgCedulaPacLibre('✓ Datos completados');
    } catch (e) {
      setMsgCedulaPacLibre(e.response?.data?.error || 'No se pudo consultar la cédula');
    }
    setBuscandoCedulaPacLibre(false);
  };
  const guardarNuevoPacLibre = async () => {
    if (!nuevoPacLibre.APELLIDO_PATERNO.trim() || !nuevoPacLibre.PRIMER_NOMBRE.trim() || !nuevoPacLibre.C_SEXO) {
      setErrPacLibre('Apellido paterno, primer nombre y sexo son obligatorios'); return;
    }
    setGuardandoPacLibre(true); setErrPacLibre('');
    try {
      const { data } = await api.post('/pacientes', nuevoPacLibre);
      const nombreCompleto = `${nuevoPacLibre.APELLIDO_PATERNO} ${nuevoPacLibre.APELLIDO_MATERNO} ${nuevoPacLibre.PRIMER_NOMBRE}`.trim();
      setCodPacLibre(data.COD);
      setPacNombreLibre(nombreCompleto);
      setShowNuevoPacLibre(false);
      setBusqPacLibre(''); setSugsPacLibre([]);
    } catch (e) {
      setErrPacLibre(e.response?.data?.error || 'No se pudo registrar el paciente');
    }
    setGuardandoPacLibre(false);
  };
  useEffect(() => {
    if (busqPacLibre.length < 2) { setSugsPacLibre([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/pacientes?q=${encodeURIComponent(busqPacLibre)}&limit=6`); setSugsPacLibre(data); } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [busqPacLibre]);
  useEffect(() => {
    if (busqMedLibre.length < 2) { setSugsMedLibre([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/farmacia/medicamentos?q=${encodeURIComponent(busqMedLibre)}&vendible=1&limit=8`); setSugsMedLibre(data); } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [busqMedLibre]);
  const selPacLibre = (p) => {
    setCodPacLibre(p.COD);
    setPacNombreLibre(`${p.APELLIDO_PATERNO||''} ${p.APELLIDO_MATERNO||''} ${p.PRIMER_NOMBRE||''}`.trim());
    setBusqPacLibre(''); setSugsPacLibre([]);
  };
  const agregarMedLibre = (m) => {
    setBusqMedLibre(''); setSugsMedLibre([]);
    setCarritoLibre((c) => {
      const yaEsta = c.find((it) => it.COD === m.COD);
      if (yaEsta) return c.map((it) => it.COD === m.COD ? { ...it, CANTIDAD: it.CANTIDAD + 1 } : it);
      return [...c, { COD: m.COD, MEDICAMENTO: m.MEDICAMENTO, PRECIO_VENTA: m.PRECIO_VENTA, STOCK: m.STOCK, CANTIDAD: 1, DESCUENTO_PCT: '', DESCUENTO_FIJO: '' }];
    });
  };
  const cambiarCantidadLibre = (cod, val) => {
    setCarritoLibre((c) => c.map((it) => it.COD === cod ? { ...it, CANTIDAD: val } : it));
  };
  const cambiarDescuentoLibre = (cod, campo, val) => {
    setCarritoLibre((c) => c.map((it) => it.COD === cod ? { ...it, [campo]: val } : it));
  };
  const cambiarPrecioLibre = (cod, val) => {
    setCarritoLibre((c) => c.map((it) => it.COD === cod ? { ...it, PRECIO_VENTA: val } : it));
  };
  const calcDescuentoLibre = (it) => {
    const lineTotal = (parseFloat(it.PRECIO_VENTA) || 0) * (parseFloat(it.CANTIDAD) || 0);
    const monto = (parseFloat(it.DESCUENTO_FIJO) || 0) + lineTotal * (parseFloat(it.DESCUENTO_PCT) || 0) / 100;
    return Math.max(0, Math.min(monto, lineTotal));
  };
  const quitarMedLibre = (cod) => setCarritoLibre((c) => c.filter((it) => it.COD !== cod));
  const totalLibre = carritoLibre.reduce((acc, it) => acc + (parseFloat(it.PRECIO_VENTA) || 0) * (parseFloat(it.CANTIDAD) || 0) - calcDescuentoLibre(it), 0);
  const cerrarVentaLibre = () => {
    setModoLibre(false); setCodPacLibre(''); setPacNombreLibre(''); setCarritoLibre([]);
    setNotaLibre(''); setMsgLibre(''); setBusqPacLibre(''); setBusqMedLibre('');
  };
  const dispensarLibre = async () => {
    setMsgLibre('');
    if (!codPacLibre) { setMsgLibre('Selecciona un paciente'); return; }
    if (!carritoLibre.length) { setMsgLibre('Agrega al menos un medicamento'); return; }
    for (const it of carritoLibre) {
      const c = parseFloat(it.CANTIDAD);
      if (!c || c <= 0) { setMsgLibre(`Ingresa una cantidad válida para "${it.MEDICAMENTO}"`); return; }
      if (c > (parseFloat(it.STOCK) || 0)) { setMsgLibre(`"${it.MEDICAMENTO}": stock disponible ${it.STOCK}`); return; }
    }
    setEnviandoLibre(true);
    try {
      await api.post('/farmacia/dispensar', {
        COD_PACIENTE: codPacLibre,
        COD_RECETA: null,
        OBSERVACIONES: notaLibre?.trim() ? `Receta externa: ${notaLibre.trim()}` : 'Venta libre / receta externa',
        items: carritoLibre.map((it) => ({ COD_MEDICAMENTO: it.COD, CANTIDAD: it.CANTIDAD, DESCUENTO: calcDescuentoLibre(it), PRECIO_VENTA: it.PRECIO_VENTA })),
      });
      setMsgLibre('✓ Despacho registrado correctamente');
      const pacDestino = codPacLibre;
      setCarritoLibre([]); setNotaLibre('');
      // Igual que ya hace la dispensación por receta (dispensar() más abajo):
      // ir directo a Facturación con el paciente y lo recién despachado ya
      // cargado, listo para cobrar -- antes esta ruta (venta libre / receta
      // externa) se quedaba en la misma pantalla sin avanzar a cobrar.
      navigate(`/facturacion?paciente=${pacDestino}`);
    } catch (e) {
      setMsgLibre('Error: ' + (e.response?.data?.error || e.message));
    }
    setEnviandoLibre(false);
  };

  const cargarCola = useCallback(async () => {
    setCargandoCola(true);
    try {
      const { data } = await api.get('/farmacia/recetas-pendientes');
      setCola(data);
    } catch (e) {
      setMsg('Error: ' + (e.response?.data?.error || e.message));
    }
    setCargandoCola(false);
  }, []);

  useEffect(() => { cargarCola(); }, [cargarCola]);

  const buscar = useCallback(async (cod, pacienteCod, pacienteNom) => {
    const cr = cod ?? codReceta;
    if (!cr) return;
    setCargando(true); setMsg('');
    try {
      const { data } = await api.get(`/farmacia/receta/${cr}`);
      setItems(data);
      const disponibles = data.filter((i) => i.disponible);
      setSel(disponibles.map((i) => i.COD_DETALLE_RECETA));
      const cant = {};
      disponibles.forEach((i) => { cant[i.COD_DETALLE_RECETA] = i.cantidadMaxDispensar; });
      setCantidades(cant);
      if (pacienteCod) setCodPaciente(pacienteCod);
      if (pacienteNom) setPacienteNombre(pacienteNom);
      try {
        const { data: hist } = await api.get(`/farmacia/receta/${cr}/historial`);
        setHistorial(hist);
      } catch (_) { setHistorial([]); }
    } catch (e) {
      setMsg('Error: ' + (e.response?.data?.error || e.message));
      setItems([]);
    }
    setCargando(false);
  }, [codReceta]);

  useEffect(() => { if (codRecetaInicial) buscar(codRecetaInicial); }, []); // eslint-disable-line

  const abrirReceta = (r) => {
    setCodReceta(String(r.COD_RECETA));
    buscar(r.COD_RECETA, r.COD_PACIENTE, r.PACIENTE);
  };

  const volverACola = () => {
    setCodReceta(''); setCodPaciente(''); setPacienteNombre(''); setItems([]); setSel([]); setCantidades({}); setHistorial([]); setMsg('');
    cargarCola();
  };

  const anularDispensacion = async (codCabecera) => {
    const motivo = window.prompt('Motivo de la anulación (ej: duplicado por doble clic):');
    if (motivo === null) return; // canceló
    setAnulando(codCabecera); setMsg('');
    try {
      await api.post(`/farmacia/dispensacion/${codCabecera}/anular`, { motivo });
      setMsg('✓ Dispensación anulada, stock devuelto al Kardex');
      buscar(codReceta);
    } catch (e) {
      setMsg('Error: ' + (e.response?.data?.error || e.message));
    }
    setAnulando(null);
  };

  // ── "No despachar": el paciente no quiere el/los medicamento(s) —
  // distinto de "Anular", que deshace una dispensación ya entregada y
  // mueve stock. Esto es ANTES de entregar nada, así que no toca stock.
  // No se quiere NINGUNA ventana emergente (ni la nativa del navegador, ni
  // un cuadro propio) -- el clic guarda directo, por interno, el motivo fijo
  // "No desea el paciente", sin pedirle nada a quien despacha.
  const [noDespachando, setNoDespachando] = useState(null); // COD_RECETA en proceso (desde la cola)
  const MOTIVO_NO_DESPACHO_DEFAULT = 'No desea el paciente';
  const noDespacharDesdeCola = async (r, e) => {
    e.stopPropagation();
    setNoDespachando(r.COD_RECETA); setMsg('');
    try {
      const { data } = await api.post(`/farmacia/receta/${r.COD_RECETA}/no-despachar`, { MOTIVO: MOTIVO_NO_DESPACHO_DEFAULT });
      setMsg(`✓ ${data.mensaje}`);
      cargarCola();
    } catch (e2) {
      setMsg('Error: ' + (e2.response?.data?.error || e2.message));
    }
    setNoDespachando(null);
  };
  const noDespacharItem = async (it) => {
    setMsg('');
    try {
      const { data } = await api.post(`/farmacia/receta/${codReceta}/no-despachar`, {
        COD_DETALLE_RECETA: [it.COD_DETALLE_RECETA], MOTIVO: MOTIVO_NO_DESPACHO_DEFAULT,
      });
      setMsg(`✓ ${data.mensaje}`);
      buscar(codReceta);
    } catch (e) {
      setMsg('Error: ' + (e.response?.data?.error || e.message));
    }
  };

  const colaConEspera = cola.map((r) => {
    const minEspera = Math.max(0, Math.round((Date.now() - new Date(r.FECHA).getTime()) / 60000));
    const colorEspera = minEspera > 30 ? 'var(--red)' : minEspera > 15 ? 'var(--amber)' : 'var(--green)';
    const bgEspera = minEspera > 30 ? 'var(--red-lt)' : minEspera > 15 ? 'var(--amber-lt)' : 'var(--green-lt)';
    const estadoLabel = minEspera > 30 ? 'Urgente' : minEspera > 15 ? 'Esperando' : 'En cola';
    return { ...r, minEspera, colorEspera, bgEspera, estadoLabel };
  });
  const medicosCola = [...new Map(cola.filter(r => r.COD_MEDICO).map(r => [r.COD_MEDICO, r.MEDICO])).entries()]
    .sort((a, b) => a[1].localeCompare(b[1]));
  const colaFiltrada = colaConEspera
    .filter((r) => {
      if (soloUrgentes && r.minEspera <= 30) return false;
      if (filtroMedico && r.COD_MEDICO !== filtroMedico) return false;
      if (!busqueda.trim()) return true;
      const q = busqueda.trim().toLowerCase();
      return r.PACIENTE.toLowerCase().includes(q)
        || (r.IDENTIFICACION || '').includes(q)
        || String(r.COD_RECETA).includes(q);
    })
    .sort((a, b) => ordenCola === 'antiguo' ? b.minEspera - a.minEspera : a.minEspera - b.minEspera);
  const totalCola = colaFiltrada.length;
  const totalPagCola = Math.max(1, Math.ceil(totalCola / TAM_COLA));
  const paginaColaActual = Math.min(paginaCola, totalPagCola);
  const iniFilaCola = totalCola === 0 ? 0 : (paginaColaActual - 1) * TAM_COLA + 1;
  const finFilaCola = Math.min(paginaColaActual * TAM_COLA, totalCola);
  const colaPagina = colaFiltrada.slice((paginaColaActual - 1) * TAM_COLA, finFilaCola);
  const numsPaginaCola = () => {
    const start = Math.max(1, paginaColaActual - 2), end = Math.min(totalPagCola, paginaColaActual + 2);
    const nums = [];
    for (let p = start; p <= end; p++) nums.push(p);
    return nums;
  };
  useEffect(() => { setPaginaCola(1); }, [busqueda, soloUrgentes, filtroMedico, ordenCola]);

  const [descItems, setDescItems] = useState({});
  const setDescuento = (cod, campo, val) => setDescItems((d) => ({ ...d, [cod]: { ...(d[cod]||{}), [campo]: val } }));
  const [precioItems, setPrecioItems] = useState({});
  const setPrecioItem = (cod, val) => setPrecioItems((p) => ({ ...p, [cod]: val }));
  const precioEfectivo = (it) => {
    const ov = parseFloat(precioItems[it.COD_DETALLE_RECETA]);
    return (!isNaN(ov) && ov >= 0) ? ov : (parseFloat(it.PRECIO_VENTA) || 0);
  };
  const calcDescuento = (it) => {
    const lineTotal = precioEfectivo(it) * (parseFloat(cantidades[it.COD_DETALLE_RECETA]) || 0);
    const d = descItems[it.COD_DETALLE_RECETA] || {};
    const monto = (parseFloat(d.FIJO) || 0) + lineTotal * (parseFloat(d.PCT) || 0) / 100;
    return Math.max(0, Math.min(monto, lineTotal));
  };
  const toggle = (it) => {
    const cod = it.COD_DETALLE_RECETA;
    if (sel.includes(cod)) {
      setSel((s) => s.filter((x) => x !== cod));
    } else {
      setSel((s) => [...s, cod]);
      setCantidades((c) => (c[cod] != null ? c : { ...c, [cod]: it.cantidadMaxDispensar }));
    }
  };
  const setCantidad = (cod, val) => setCantidades((c) => ({ ...c, [cod]: val }));

  // ── Agregar medicamentos no recetados a esta misma dispensación ──
  // A pedido de José: el médico puede agregar medicamentos que no estaban
  // en la receta original (ej. algo que decide dar en el momento) sin tener
  // que abrir aparte una venta libre. Se agregan a la MISMA lista de items,
  // con COD_DETALLE_RECETA sintético (string "extra-N") para que toda la
  // lógica de selección/cantidad/descuento/precio ya existente (que solo usa
  // ese valor como llave, nunca lo manda tal cual salvo que sea numérico) los
  // trate igual que un ítem recetado. Al armar el POST, dispensar() manda
  // COD_DETALLE_RECETA=null para estos -- el backend ya sabe tratar eso como
  // un ítem suelto (mismo camino que usa la venta libre).
  const [busqMedExtra, setBusqMedExtra] = useState('');
  const [sugsMedExtra, setSugsMedExtra] = useState([]);
  const [mostrarAgregarExtra, setMostrarAgregarExtra] = useState(false);
  useEffect(() => {
    if (busqMedExtra.length < 2) { setSugsMedExtra([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/farmacia/medicamentos?q=${encodeURIComponent(busqMedExtra)}&vendible=1&limit=8`); setSugsMedExtra(data); } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [busqMedExtra]);
  const agregarMedExtra = (m) => {
    const key = `extra-${m.COD}`;
    setBusqMedExtra(''); setSugsMedExtra([]); setMostrarAgregarExtra(false);
    if (items.some((it) => it.COD_DETALLE_RECETA === key)) { setMsg(`"${m.MEDICAMENTO}" ya está en la lista`); return; }
    const stock = parseFloat(m.STOCK) || 0;
    setItems((prev) => [...prev, {
      COD_DETALLE_RECETA: key, ES_EXTRA: true, COD_MEDICAMENTO: m.COD,
      NOMBRE_RECETADO: m.MEDICAMENTO, NOMBRE_COMERCIAL: m.NOMBRE_COMERCIAL || '',
      INDICACION: 'Agregado en farmacia (no estaba en la receta)',
      CANTIDAD: null, CANTIDAD_DISPENSADA: 0, PENDIENTE: null,
      STOCK: stock, PRECIO_VENTA: m.PRECIO_VENTA,
      disponible: stock > 0, stockSuficiente: stock > 0, cantidadMaxDispensar: stock,
      noDespachado: false, C_ESTADO: 'ACT',
    }]);
    setSel((s) => [...s, key]);
    setCantidades((c) => ({ ...c, [key]: stock > 0 ? 1 : 0 }));
  };
  const quitarMedExtra = (key) => {
    setItems((prev) => prev.filter((it) => it.COD_DETALLE_RECETA !== key));
    setSel((s) => s.filter((x) => x !== key));
    setCantidades((c) => { const n = { ...c }; delete n[key]; return n; });
  };

  const total = items
    .filter((i) => sel.includes(i.COD_DETALLE_RECETA))
    .reduce((acc, i) => acc + precioEfectivo(i) * (parseFloat(cantidades[i.COD_DETALLE_RECETA]) || 0) - calcDescuento(i), 0);

  const dispensar = async () => {
    if (!codPaciente) { setMsg('Falta el código del paciente'); return; }
    const paraDispensar = items.filter((i) => sel.includes(i.COD_DETALLE_RECETA));
    if (!paraDispensar.length) { setMsg('Selecciona al menos un ítem disponible'); return; }
    for (const i of paraDispensar) {
      const c = parseFloat(cantidades[i.COD_DETALLE_RECETA]);
      if (!c || c <= 0) { setMsg(`Ingresa una cantidad válida para "${i.NOMBRE_RECETADO}"`); return; }
      const stockDisp = parseFloat(i.STOCK) || 0;
      if (c > stockDisp + 0.0001) { setMsg(`"${i.NOMBRE_RECETADO}": no hay stock suficiente (disponible: ${stockDisp})`); return; }
    }
    // El paciente puede pedir más o menos de lo recetado (p.ej. fracciones de
    // tableta que no se pueden entregar sueltas, o simplemente no quiere/necesita
    // todo). Ya no se bloquea si la cantidad no coincide exactamente con lo
    // recetado/pendiente — solo se pide confirmar, y el límite real sigue siendo
    // el stock disponible (verificado arriba).
    const difieren = paraDispensar.filter((i) => !i.ES_EXTRA && Math.abs(parseFloat(cantidades[i.COD_DETALLE_RECETA]) - i.cantidadMaxDispensar) > 0.0001);
    if (difieren.length) {
      const detalle = difieren.map((i) =>
        `• ${i.NOMBRE_RECETADO}: vas a despachar ${cantidades[i.COD_DETALLE_RECETA]} (recetado/pendiente: ${i.cantidadMaxDispensar})`
      ).join('\n');
      if (!window.confirm(`Estas cantidades no coinciden exactamente con lo recetado:\n\n${detalle}\n\n¿Continuar de todas formas?`)) return;
    }
    setEnviando(true); setMsg('');
    try {
      await api.post('/farmacia/dispensar', {
        COD_RECETA: codReceta,
        COD_PACIENTE: codPaciente,
        items: paraDispensar.map((i) => ({
          // Los agregados en farmacia (ES_EXTRA) usan una llave sintética
          // ("extra-N") solo para la selección/cantidad en pantalla -- al
          // backend se manda COD_DETALLE_RECETA=null, igual que ya hace la
          // venta libre, para que los trate como un ítem suelto y no intente
          // buscarlo en detalles_receta.
          COD_DETALLE_RECETA: i.ES_EXTRA ? null : i.COD_DETALLE_RECETA,
          COD_MEDICAMENTO: i.COD_MEDICAMENTO,
          CANTIDAD: cantidades[i.COD_DETALLE_RECETA],
          DESCUENTO: calcDescuento(i),
          PRECIO_VENTA: precioEfectivo(i),
        })),
      });
      setMsg('✓ Dispensación registrada correctamente');
      navigate(`/facturacion?paciente=${codPaciente}`);
    } catch (e) {
      setMsg('Error: ' + (e.response?.data?.error || e.message));
    }
    setEnviando(false);
  };

  // ── Vista de cola (sin receta abierta) ──
  if (!codReceta) {
    if (modoLibre) {
      return (
        <div>
          <button className="btn btn-sm" onClick={cerrarVentaLibre}
            style={{ marginBottom: 10 }}>
            ← Volver a recetas pendientes
          </button>
          <div style={{ background: 'var(--amber-lt)', border: '1px solid var(--amber)', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: 'var(--amber)', marginBottom: 16 }}>
            🆓 Venta libre / receta externa — para despachar medicamentos sin una receta generada en NAREMI MEDICAL BUSINESS SOLUTIONS S.A.S. (ej. receta de otro médico o centro).
          </div>
          {!codPacLibre ? (
            <div style={{ position: 'relative' }}>
              <input className="form-control" placeholder="Buscar paciente por nombre o cédula..." value={busqPacLibre}
                onChange={(e) => setBusqPacLibre(e.target.value)} autoFocus
                style={{ padding: '10px 14px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
              {sugsPacLibre.length > 0 && (
                <div style={{ border: '1px solid var(--border)', borderRadius: 8, marginTop: 6, background: 'var(--surface)', boxShadow: '0 4px 14px rgba(0,0,0,.08)' }}>
                  {sugsPacLibre.map((p) => (
                    <div key={p.COD} onClick={() => selPacLibre(p)}
                      style={{ padding: '9px 14px', fontSize: 12.5, cursor: 'pointer', borderBottom: '1px solid var(--bg)' }}>
                      <div style={{ fontWeight: 700, color: 'var(--navy)' }}>{`${p.APELLIDO_PATERNO||''} ${p.APELLIDO_MATERNO||''} ${p.PRIMER_NOMBRE||''}`.trim()}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)' }}>CI {p.IDENTIFICACION}</div>
                    </div>
                  ))}
                </div>
              )}
              <button onClick={abrirNuevoPacLibre}
                style={{ marginTop: 10, fontSize: 12, color: 'var(--navy)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontWeight: 600 }}>
                + Registrar paciente nuevo (no encontrado / primera vez en el centro)
              </button>
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--navy)' }}>{pacNombreLibre}</div>
                <button className="btn btn-sm" onClick={() => { setCodPacLibre(''); setPacNombreLibre(''); setCarritoLibre([]); }}>Cambiar paciente</button>
              </div>
              <div style={{ position: 'relative', marginBottom: 16 }}>
                <input className="form-control" placeholder="Buscar medicamento por nombre..." value={busqMedLibre}
                  onChange={(e) => setBusqMedLibre(e.target.value)}
                  style={{ padding: '10px 14px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
                {sugsMedLibre.length > 0 && (
                  <div style={{ border: '1px solid var(--border)', borderRadius: 8, marginTop: 6, background: 'var(--surface)', boxShadow: '0 4px 14px rgba(0,0,0,.08)' }}>
                    {sugsMedLibre.map((m) => (
                      <div key={m.COD} onClick={() => agregarMedLibre(m)}
                        style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 14px', fontSize: 12.5, cursor: 'pointer', borderBottom: '1px solid var(--bg)' }}>
                        <span style={{ color: 'var(--navy)' }}>{m.MEDICAMENTO}{m.NOMBRE_COMERCIAL ? ` (${m.NOMBRE_COMERCIAL})` : ''}</span>
                        <span style={{ color: 'var(--text3)' }}>{m.SE_PUEDE_VENDER ? `PVP $${Number(m.PRECIO_VENTA).toFixed(2)} · costo $${Number(m.COSTO_PROMEDIO||0).toFixed(2)} · stock ${m.STOCK}` : 'no vendible'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {carritoLibre.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--text3)', textAlign: 'center', padding: 30 }}>Aún no has agregado medicamentos.</div>
              ) : (
                <div style={{ marginBottom: 16 }}>
                  {carritoLibre.map((it) => (
                    <div key={it.COD} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 14px', marginBottom: 8 }}>
                      <div style={{ flex: 1, fontSize: 13, color: 'var(--navy)' }}>{it.MEDICAMENTO}</div>
                      <input type="number" min="0.01" max={it.STOCK} step="any" value={it.CANTIDAD}
                        onChange={(e) => cambiarCantidadLibre(it.COD, e.target.value)}
                        style={{ width: 64, padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12.5, textAlign: 'center' }} />
                      <input type="number" min="0" max="100" step="any" placeholder="%" value={it.DESCUENTO_PCT ?? ''}
                        onChange={(e) => cambiarDescuentoLibre(it.COD, 'DESCUENTO_PCT', e.target.value)}
                        title="Descuento %"
                        style={{ width: 48, padding: '6px 6px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, textAlign: 'center' }} />
                      <input type="number" min="0" step="any" placeholder="$" value={it.DESCUENTO_FIJO ?? ''}
                        onChange={(e) => cambiarDescuentoLibre(it.COD, 'DESCUENTO_FIJO', e.target.value)}
                        title="Descuento fijo $"
                        style={{ width: 54, padding: '6px 6px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, textAlign: 'center' }} />
                      <input type="number" min="0" step="any" value={it.PRECIO_VENTA ?? ''}
                        onChange={(e) => cambiarPrecioLibre(it.COD, e.target.value)}
                        title="PVP unitario (editable)"
                        style={{ width: 64, padding: '6px 8px', border: '1px solid var(--amber)', borderRadius: 6, fontSize: 12.5, textAlign: 'center', fontWeight: 600 }} />
                      <div style={{ width: 70, textAlign: 'right', fontSize: 12.5, fontWeight: 700, color: 'var(--navy)' }}>
                        ${(((parseFloat(it.PRECIO_VENTA) || 0) * (parseFloat(it.CANTIDAD) || 0)) - calcDescuentoLibre(it)).toFixed(2)}
                      </div>
                      <button className="act-btn" onClick={() => quitarMedLibre(it.COD)}
                        style={{ color: 'var(--red)' }}>✕</button>
                    </div>
                  ))}
                </div>
              )}
              <textarea placeholder="Nota (médico y centro que prescribió, opcional)" value={notaLibre}
                onChange={(e) => setNotaLibre(e.target.value)} rows={2}
                style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12.5, boxSizing: 'border-box', marginBottom: 14, resize: 'vertical' }} />
              {msgLibre && <div style={{ fontSize: 12.5, color: msgLibre.startsWith('✓') ? 'var(--green)' : 'var(--red)', marginBottom: 12 }}>{msgLibre}</div>}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid var(--border)', paddingTop: 14 }}>
                <div style={{ fontSize: 13, color: 'var(--text3)' }}>Total: <strong style={{ color: 'var(--navy)', fontSize: 15 }}>${totalLibre.toFixed(2)}</strong></div>
                <button className="btn btn-primary" onClick={dispensarLibre} disabled={enviandoLibre || !carritoLibre.length}
                  style={{ padding: '10px 20px', fontSize: 13, fontWeight: 700, opacity: enviandoLibre || !carritoLibre.length ? .6 : 1 }}>
                  {enviandoLibre ? 'Procesando...' : 'Dispensar y cobrar →'}
                </button>
              </div>
            </div>
          )}
          {showNuevoPacLibre && (
            <div onClick={(e) => e.target === e.currentTarget && setShowNuevoPacLibre(false)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
              <div style={{ background: 'var(--surface)', borderRadius: 10, width: '100%', maxWidth: 420, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,.25)' }}>
                <div style={{ padding: '13px 18px', borderRadius: '10px 10px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--navy)', color: 'var(--surface)' }}>
                  <span style={{ fontWeight: 700, fontSize: 13 }}>Registrar paciente nuevo</span>
                  <span style={{ cursor: 'pointer', fontSize: 18, opacity: .8 }} onClick={() => setShowNuevoPacLibre(false)}>✕</span>
                </div>
                <div style={{ padding: 18 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                    <div>
                      <label style={{ fontSize: 10, display: 'block', marginBottom: 3, color: 'var(--text3)' }}>Tipo identificación</label>
                      <select style={{ width: '100%', padding: '7px 8px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' }}
                        value={nuevoPacLibre.C_TIPO_IDENTIFICACION}
                        onChange={(e) => setNuevoPacLibre((p) => ({ ...p, C_TIPO_IDENTIFICACION: e.target.value }))}>
                        <option value="CDL">Cédula</option>
                        <option value="RUC">RUC</option>
                        <option value="PSP">Pasaporte</option>
                        <option value="NAP">N/A</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 10, display: 'block', marginBottom: 3, color: 'var(--text3)' }}>Identificación</label>
                      <input style={{ width: '100%', padding: '7px 8px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' }}
                        value={nuevoPacLibre.IDENTIFICACION}
                        onChange={(e) => setNuevoPacLibre((p) => ({ ...p, IDENTIFICACION: e.target.value }))} />
                      {nuevoPacLibre.C_TIPO_IDENTIFICACION === 'CDL' && (
                        <>
                          <button type="button" className="btn btn-sm" disabled={buscandoCedulaPacLibre || !/^\d{10}$/.test(nuevoPacLibre.IDENTIFICACION || '')}
                            onClick={buscarCedulaPacLibre}
                            style={{ marginTop: 4, fontSize: 10, padding: '2px 6px' }}>
                            {buscandoCedulaPacLibre ? 'Buscando...' : '🔍 Autocompletar'}
                          </button>
                          {msgCedulaPacLibre && (
                            <div style={{ fontSize: 10, marginTop: 2, color: msgCedulaPacLibre.startsWith('✓') ? 'var(--green)' : 'var(--red)' }}>{msgCedulaPacLibre}</div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                    <div>
                      <label style={{ fontSize: 10, display: 'block', marginBottom: 3, color: 'var(--text3)' }}>Apellido paterno *</label>
                      <input style={{ width: '100%', padding: '7px 8px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' }}
                        value={nuevoPacLibre.APELLIDO_PATERNO}
                        onChange={(e) => setNuevoPacLibre((p) => ({ ...p, APELLIDO_PATERNO: e.target.value }))} />
                    </div>
                    <div>
                      <label style={{ fontSize: 10, display: 'block', marginBottom: 3, color: 'var(--text3)' }}>Apellido materno</label>
                      <input style={{ width: '100%', padding: '7px 8px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' }}
                        value={nuevoPacLibre.APELLIDO_MATERNO}
                        onChange={(e) => setNuevoPacLibre((p) => ({ ...p, APELLIDO_MATERNO: e.target.value }))} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                    <div>
                      <label style={{ fontSize: 10, display: 'block', marginBottom: 3, color: 'var(--text3)' }}>Primer nombre *</label>
                      <input style={{ width: '100%', padding: '7px 8px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' }}
                        value={nuevoPacLibre.PRIMER_NOMBRE}
                        onChange={(e) => setNuevoPacLibre((p) => ({ ...p, PRIMER_NOMBRE: e.target.value }))} />
                    </div>
                    <div>
                      <label style={{ fontSize: 10, display: 'block', marginBottom: 3, color: 'var(--text3)' }}>Segundo nombre</label>
                      <input style={{ width: '100%', padding: '7px 8px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' }}
                        value={nuevoPacLibre.SEGUNDO_NOMBRE}
                        onChange={(e) => setNuevoPacLibre((p) => ({ ...p, SEGUNDO_NOMBRE: e.target.value }))} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                    <div>
                      <label style={{ fontSize: 10, display: 'block', marginBottom: 3, color: 'var(--text3)' }}>Sexo *</label>
                      <select style={{ width: '100%', padding: '7px 8px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' }}
                        value={nuevoPacLibre.C_SEXO}
                        onChange={(e) => setNuevoPacLibre((p) => ({ ...p, C_SEXO: e.target.value }))}>
                        <option value="">Seleccionar...</option>
                        <option value="F">Femenino</option>
                        <option value="M">Masculino</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 10, display: 'block', marginBottom: 3, color: 'var(--text3)' }}>Teléfono</label>
                      <input style={{ width: '100%', padding: '7px 8px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' }}
                        value={nuevoPacLibre.TELEFONO}
                        onChange={(e) => setNuevoPacLibre((p) => ({ ...p, TELEFONO: e.target.value }))} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                    <div>
                      <label style={{ fontSize: 10, display: 'block', marginBottom: 3, color: 'var(--text3)' }}>Correo electrónico</label>
                      <input type="email" style={{ width: '100%', padding: '7px 8px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' }}
                        value={nuevoPacLibre.EMAIL}
                        onChange={(e) => setNuevoPacLibre((p) => ({ ...p, EMAIL: e.target.value }))} />
                    </div>
                    <div>
                      <label style={{ fontSize: 10, display: 'block', marginBottom: 3, color: 'var(--text3)' }}>Dirección</label>
                      <input style={{ width: '100%', padding: '7px 8px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' }}
                        value={nuevoPacLibre.DIRECCION_DOMICILIO}
                        onChange={(e) => setNuevoPacLibre((p) => ({ ...p, DIRECCION_DOMICILIO: e.target.value }))} />
                    </div>
                  </div>
                  {errPacLibre && <div style={{ fontSize: 11.5, color: 'var(--red)', marginBottom: 10 }}>{errPacLibre}</div>}
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button className="btn btn-sm" onClick={() => setShowNuevoPacLibre(false)}>
                      Cancelar
                    </button>
                    <button className="btn btn-primary btn-sm" onClick={guardarNuevoPacLibre} disabled={guardandoPacLibre}
                      style={{ fontWeight: 700, opacity: guardandoPacLibre ? .6 : 1 }}>
                      {guardandoPacLibre ? 'Guardando...' : 'Guardar y continuar'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }
    return (
      <div>
        {!cargandoCola && cola.length > 0 && (
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-icon" style={{ background: 'var(--blue-lt)', color: 'var(--blue-mid)' }}>📋</div>
              <div>
                <div className="metric-value">{cola.length}</div>
                <div className="metric-label">Recetas pendientes</div>
              </div>
            </div>
            <div className="metric-card">
              <div className="metric-icon" style={{ background: 'var(--amber-lt)', color: 'var(--amber)' }}>💊</div>
              <div>
                <div className="metric-value">{cola.reduce((a, r) => a + (parseInt(r.ITEMS_PENDIENTES) || 0), 0)}</div>
                <div className="metric-label">Ítems por despachar</div>
              </div>
            </div>
          </div>
        )}

        {/* ── Subtabs ── */}
        <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)', marginBottom: 16 }}>
          <button onClick={() => setModoLibre(false)}
            style={{ padding: '9px 16px', fontSize: 12.5, fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--navy)', borderBottom: '2px solid var(--navy)', marginBottom: -1 }}>
            Cola de dispensación
          </button>
          <button onClick={() => setModoLibre(true)}
            style={{ padding: '9px 16px', fontSize: 12.5, fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text3)', borderBottom: '2px solid transparent', marginBottom: -1 }}>
            Venta libre / paciente externo
          </button>
        </div>

        {/* ── Toolbar ── */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <input className="form-control" placeholder="🔎 Buscar paciente, cédula o receta..." value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            style={{ padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12.5, flex: 1, minWidth: 200, boxSizing: 'border-box' }} />
          <button onClick={() => setSoloUrgentes((v) => !v)}
            style={{ padding: '8px 14px', fontSize: 11.5, fontWeight: 700, borderRadius: 20, cursor: 'pointer', whiteSpace: 'nowrap',
              border: soloUrgentes ? '1px solid var(--red)' : '1px solid var(--border)',
              background: soloUrgentes ? 'var(--red-lt)' : 'var(--surface)',
              color: soloUrgentes ? 'var(--red)' : 'var(--text2)' }}>
            🔴 Solo urgentes
          </button>
          <select value={filtroMedico} onChange={(e) => setFiltroMedico(e.target.value)}
            style={{ padding: '9px 10px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 11.5, background: 'var(--surface)' }}>
            <option value="">Todos los médicos</option>
            {medicosCola.map(([cod, nom]) => <option key={cod} value={cod}>{nom}</option>)}
          </select>
          <select value={ordenCola} onChange={(e) => setOrdenCola(e.target.value)}
            style={{ padding: '9px 10px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 11.5, background: 'var(--surface)' }}>
            <option value="antiguo">Ordenar: espera mayor primero</option>
            <option value="reciente">Ordenar: espera menor primero</option>
          </select>
        </div>
        {msg && <div style={{ fontSize: 12.5, color: 'var(--red)', marginBottom: 14 }}>{msg}</div>}

        {cargandoCola ? (
          <div className="spinner" />
        ) : colaFiltrada.length === 0 ? (
          <div className="empty-state" style={{ background: 'var(--surface)', border: '1px dashed var(--border2)', borderRadius: 12 }}>
            <div className="empty-icon">{busqueda || soloUrgentes || filtroMedico ? '🔍' : '✓'}</div>
            {busqueda || soloUrgentes || filtroMedico ? 'Sin resultados para ese filtro.' : 'No hay recetas pendientes por despachar. Todo al día.'}
          </div>
        ) : (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--blue-lt)' }}>
                  {['Espera', 'Paciente', 'Médico', 'Receta', 'Ítems', 'Hora', 'Estado', ''].map((h) => (
                    <th key={h} style={{ padding: '8px 12px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                      letterSpacing: '.4px', color: 'var(--navy)', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {colaPagina.map((r) => (
                  <tr key={r.COD_RECETA} style={{ borderBottom: '1px solid var(--bg)', cursor: 'pointer' }} onClick={() => abrirReceta(r)}>
                    <td style={{ padding: '8px 12px', fontSize: 12, fontWeight: 700, color: r.colorEspera, whiteSpace: 'nowrap' }}>
                      <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: r.colorEspera, marginRight: 6 }} />
                      {r.minEspera} min
                    </td>
                    <td style={{ padding: '8px 12px', fontSize: 12 }}>
                      <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{r.PACIENTE}</div>
                      <div style={{ fontSize: 10.5, color: 'var(--text3)' }}>CI {r.IDENTIFICACION}</div>
                    </td>
                    <td style={{ padding: '8px 12px', fontSize: 11.5, color: 'var(--text2)' }}>{r.MEDICO || '—'}</td>
                    <td style={{ padding: '8px 12px', fontSize: 12 }}>#{r.COD_RECETA}</td>
                    <td style={{ padding: '8px 12px', fontSize: 12 }}>{r.ITEMS_PENDIENTES}</td>
                    <td style={{ padding: '8px 12px', fontSize: 11.5, color: 'var(--text3)' }}>
                      {new Date(r.FECHA).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td style={{ padding: '8px 12px', fontSize: 11 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600,
                        background: r.bgEspera, color: r.colorEspera }}>
                        {r.estadoLabel}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px', fontSize: 11, whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-primary btn-sm" onClick={(e) => { e.stopPropagation(); abrirReceta(r); }}>Dispensar</button>
                        <button className="btn btn-sm" disabled={noDespachando === r.COD_RECETA}
                          onClick={(e) => noDespacharDesdeCola(r, e)}
                          title="El paciente no desea el/los medicamento(s) de esta receta"
                          style={{ borderColor: 'var(--red-lt)', color: 'var(--red)', fontWeight: 600 }}>
                          {noDespachando === r.COD_RECETA ? '...' : 'No despachar'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px',
              borderTop: '1px solid var(--border)', background: 'var(--surface2)', fontSize: 12, color: 'var(--text3)', flexWrap: 'wrap', gap: 10 }}>
              <span>{totalCola === 0 ? 'Sin resultados' : `Mostrando ${iniFilaCola}–${finFilaCola} de ${totalCola}`}</span>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <button className="btn btn-sm" disabled={paginaColaActual === 1} onClick={() => setPaginaCola(1)}>«</button>
                <button className="btn btn-sm" disabled={paginaColaActual === 1} onClick={() => setPaginaCola((p) => p - 1)}>‹</button>
                {numsPaginaCola().map((p) => (
                  <button key={p} onClick={() => setPaginaCola(p)}
                    className="btn btn-sm"
                    style={p === paginaColaActual ? { background: 'var(--navy)', color: 'var(--surface)', borderColor: 'var(--navy)', fontWeight: 700 } : undefined}>
                    {p}
                  </button>
                ))}
                <button className="btn btn-sm" disabled={paginaColaActual === totalPagCola} onClick={() => setPaginaCola((p) => p + 1)}>›</button>
                <button className="btn btn-sm" disabled={paginaColaActual === totalPagCola} onClick={() => setPaginaCola(totalPagCola)}>»</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Vista de detalle (receta abierta) ──
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
        <button className="btn btn-sm" onClick={volverACola}
          style={{ fontWeight: 600, color: 'var(--navy)' }}>
          ← Volver a la cola
        </button>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--navy)' }}>{pacienteNombre || `Receta #${codReceta}`}</div>
        {cargando && <span style={{ fontSize: 12, color: 'var(--text3)' }}>Cargando...</span>}
      </div>

      {msg && <div style={{ fontSize: 12.5, color: msg.startsWith('✓') ? 'var(--green)' : 'var(--red)', marginBottom: 14 }}>{msg}</div>}

      <div style={{ marginBottom: 14 }}>
        {!mostrarAgregarExtra ? (
          <button className="btn btn-sm" onClick={() => setMostrarAgregarExtra(true)}
            style={{ color: 'var(--navy)', fontWeight: 600 }}>
            + Agregar medicamento (no estaba en la receta)
          </button>
        ) : (
          <div style={{ position: 'relative', maxWidth: 420 }}>
            <input className="form-control" placeholder="Buscar medicamento por nombre..." value={busqMedExtra}
              onChange={(e) => setBusqMedExtra(e.target.value)} autoFocus
              style={{ padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12.5, width: '100%', boxSizing: 'border-box' }} />
            <button className="btn btn-sm" onClick={() => { setMostrarAgregarExtra(false); setBusqMedExtra(''); setSugsMedExtra([]); }}
              style={{ position: 'absolute', right: 6, top: 5 }}>✕</button>
            {sugsMedExtra.length > 0 && (
              <div style={{ border: '1px solid var(--border)', borderRadius: 8, marginTop: 6, background: 'var(--surface)', boxShadow: '0 4px 14px rgba(0,0,0,.08)', position: 'absolute', zIndex: 5, width: '100%' }}>
                {sugsMedExtra.map((m) => (
                  <div key={m.COD} onClick={() => agregarMedExtra(m)}
                    style={{ padding: '9px 14px', fontSize: 12.5, cursor: 'pointer', borderBottom: '1px solid var(--bg)', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span>{m.MEDICAMENTO}{m.NOMBRE_COMERCIAL ? ` (${m.NOMBRE_COMERCIAL})` : ''}</span>
                    <span style={{ color: 'var(--text3)', whiteSpace: 'nowrap' }}>Stock: {m.STOCK}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {items.map((it) => {
        const seleccionado = sel.includes(it.COD_DETALLE_RECETA);
        const stock = parseFloat(it.STOCK) || 0;
        let badge;
        if (it.noDespachado) {
          badge = { bg: 'var(--bg)', c: 'var(--text3)', t: '✕ No despachado' };
        } else if (!it.COD_MEDICAMENTO) {
          badge = { bg: 'var(--red-lt)', c: 'var(--red)', t: '✕ NO EN CATÁLOGO' };
        } else if (stock <= 0) {
          badge = { bg: 'var(--red-lt)', c: 'var(--red)', t: '✕ SIN STOCK' };
        } else if (!it.stockSuficiente) {
          badge = { bg: 'var(--amber-lt)', c: 'var(--amber)', t: `⚠ STOCK PARCIAL (${it.cantidadMaxDispensar} de ${it.PENDIENTE})` };
        } else {
          badge = { bg: 'var(--green-lt)', c: 'var(--green)', t: `✓ EN STOCK (${stock} disp.)` };
        }
        return (
          <div key={it.COD_DETALLE_RECETA} style={{
            display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', background: it.disponible ? 'var(--surface)' : 'var(--red-lt)',
            border: `1px solid ${it.disponible ? 'var(--border)' : 'var(--red-lt)'}`, borderRadius: 12, padding: '14px 16px', marginBottom: 10,
          }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--navy)' }}>{it.NOMBRE_RECETADO}{it.NOMBRE_COMERCIAL ? ` (${it.NOMBRE_COMERCIAL})` : ''}</div>
              <div style={{ fontSize: 11, color: it.ES_EXTRA ? 'var(--navy)' : 'var(--text3)', marginTop: 2 }}>{it.INDICACION}{it.CANTIDAD != null ? ` · Recetado: ${it.CANTIDAD}` : ''}</div>
              {parseFloat(it.CANTIDAD_DISPENSADA) > 0 && (
                <div style={{ fontSize: 11, color: 'var(--amber)', marginTop: 2 }}>
                  Ya entregado: {it.CANTIDAD_DISPENSADA} · Pendiente: {it.PENDIENTE}
                </div>
              )}
            </div>
            <span style={{ fontSize: 10, fontWeight: 700, background: badge.bg, color: badge.c, padding: '4px 10px', borderRadius: 999, whiteSpace: 'nowrap' }}
              title={it.noDespachado && it.MOTIVO_NO_DESPACHO ? `Motivo: ${it.MOTIVO_NO_DESPACHO}` : undefined}>
              {badge.t}
            </span>
            {it.ES_EXTRA ? (
              <button className="btn btn-sm" onClick={() => quitarMedExtra(it.COD_DETALLE_RECETA)}
                title="Quitar este medicamento agregado"
                style={{ borderColor: 'var(--red-lt)', color: 'var(--red)', fontWeight: 600, fontSize: 11 }}>
                Quitar
              </button>
            ) : it.C_ESTADO === 'ACT' && (
              <button className="btn btn-sm" onClick={() => noDespacharItem(it)}
                title="El paciente no desea este medicamento"
                style={{ borderColor: 'var(--red-lt)', color: 'var(--red)', fontWeight: 600, fontSize: 11 }}>
                No despachar
              </button>
            )}
            {it.disponible && (
              <input type="number" min="0.01" max={it.STOCK} step="any"
                title="Puede ajustarse distinto a lo recetado (el paciente puede pedir más o menos) — se pedirá confirmación si no coincide"
                disabled={!seleccionado}
                value={cantidades[it.COD_DETALLE_RECETA] ?? ''}
                onChange={(e) => setCantidad(it.COD_DETALLE_RECETA, e.target.value)}
                style={{ width: 64, padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12.5, textAlign: 'center' }} />
            )}
            {it.disponible && seleccionado && (
              <>
                <input type="number" min="0" max="100" step="any" placeholder="%"
                  value={descItems[it.COD_DETALLE_RECETA]?.PCT ?? ''}
                  onChange={(e) => setDescuento(it.COD_DETALLE_RECETA, 'PCT', e.target.value)}
                  title="Descuento %"
                  style={{ width: 48, padding: '6px 6px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, textAlign: 'center' }} />
                <input type="number" min="0" step="any" placeholder="$"
                  value={descItems[it.COD_DETALLE_RECETA]?.FIJO ?? ''}
                  onChange={(e) => setDescuento(it.COD_DETALLE_RECETA, 'FIJO', e.target.value)}
                  title="Descuento fijo $"
                  style={{ width: 54, padding: '6px 6px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, textAlign: 'center' }} />
                <input type="number" min="0" step="any"
                  value={precioItems[it.COD_DETALLE_RECETA] ?? it.PRECIO_VENTA ?? ''}
                  onChange={(e) => setPrecioItem(it.COD_DETALLE_RECETA, e.target.value)}
                  title="PVP unitario (editable)"
                  style={{ width: 64, padding: '6px 8px', border: '1px solid var(--amber)', borderRadius: 6, fontSize: 12.5, textAlign: 'center', fontWeight: 600 }} />
              </>
            )}
            <div style={{ width: 70, textAlign: 'center', fontSize: 13, fontWeight: 700, color: it.disponible ? 'var(--navy)' : 'var(--text3)' }}>
              {it.disponible ? `$${((precioEfectivo(it) * (parseFloat(cantidades[it.COD_DETALLE_RECETA]) || 0)) - calcDescuento(it)).toFixed(2)}` : 'Externa'}
            </div>
            <input type="checkbox" disabled={!it.disponible}
              checked={seleccionado} onChange={() => toggle(it)}
              style={{ width: 18, height: 18 }} />
          </div>
        );
      })}

      {items.length > 0 && (
        <>
          <div style={{ height: 1, background: 'var(--border)', margin: '18px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 11.5, color: 'var(--text3)' }}>
              {sel.length} de {items.length} ítems seleccionados para dispensar
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--navy)' }}>${total.toFixed(2)}</div>
          </div>
          <button className="btn btn-primary" onClick={dispensar} disabled={enviando}
            style={{ marginTop: 14, width: '100%', padding: 13, fontSize: 13.5, fontWeight: 700, justifyContent: 'center' }}>
            {enviando ? 'Procesando...' : 'Dispensar y cobrar →'}
          </button>
        </>
      )}

      {historial.filter((h) => h.C_ESTADO === 'ACT').length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 8 }}>
            Dispensaciones ya registradas
          </div>
          {historial.filter((h) => h.C_ESTADO === 'ACT').map((h) => (
            <div key={h.COD_CABECERA} style={{
              display: 'flex', alignItems: 'center', gap: 12, background: 'var(--surface2)', border: '1px solid var(--border)',
              borderRadius: 10, padding: '10px 14px', marginBottom: 8,
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--navy)' }}>{h.MEDICAMENTO} · {h.CANTIDAD}</div>
                <div style={{ fontSize: 10.5, color: 'var(--text3)', marginTop: 2 }}>
                  {new Date(h.FECHA).toLocaleString('es-EC')} · {h.USUARIO}
                </div>
              </div>
              <button className="btn btn-sm" onClick={() => anularDispensacion(h.COD_CABECERA)} disabled={anulando === h.COD_CABECERA}
                style={{ borderColor: 'var(--red-lt)', color: 'var(--red)', fontWeight: 600 }}>
                {anulando === h.COD_CABECERA ? 'Anulando...' : 'Anular'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Panel de precios pendientes ── */
function PanelPrecios() {
  const [lista, setLista]       = useState([]);
  const [editando, setEditando] = useState({}); // { [COD]: valorEscrito }
  const [msg, setMsg]           = useState('');

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get('/farmacia/precios-pendientes');
      setLista(data);
    } catch (e) { setMsg('Error: ' + (e.response?.data?.error || e.message)); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const validar = async (cod, precio) => {
    try {
      await api.put(`/farmacia/precios/${cod}`, { PRECIO_VENTA: precio });
      setMsg('✓ Precio validado');
      cargar();
    } catch (e) { setMsg('Error: ' + (e.response?.data?.error || e.message)); }
  };

  if (!lista.length) {
    return <div className="empty-state" style={{ background: 'var(--surface)', border: '1px dashed var(--border2)', borderRadius: 12 }}>
      <div className="empty-icon">✓</div>
      No hay precios pendientes de validar. Todo al día.
    </div>;
  }

  return (
    <div>
      {msg && <div style={{ fontSize: 12.5, color: 'var(--green)', marginBottom: 14 }}>{msg}</div>}
      {lista.map((m) => (
        <div key={m.COD} style={{
          display: 'flex', alignItems: 'center', gap: 16, background: 'var(--surface)', border: '1px solid var(--amber)',
          borderRadius: 12, padding: '14px 16px', marginBottom: 10,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--navy)' }}>{m.MEDICAMENTO}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>Costo promedio actual: ${parseFloat(m.COSTO_PROMEDIO).toFixed(4)}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 9.5, color: 'var(--text3)', textTransform: 'uppercase' }}>Precio actual</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text3)', textDecoration: 'line-through' }}>${parseFloat(m.PRECIO_VENTA).toFixed(2)}</div>
          </div>
          <div style={{ fontSize: 18, color: 'var(--text3)' }}>→</div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 9.5, color: 'var(--blue-mid)', textTransform: 'uppercase' }}>Sugerido</div>
            <input
              value={editando[m.COD] ?? m.PRECIO_SUGERIDO}
              onChange={(e) => setEditando((s) => ({ ...s, [m.COD]: e.target.value }))}
              style={{ width: 70, textAlign: 'center', fontSize: 14, fontWeight: 700, color: 'var(--navy)', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 6px' }}
            />
          </div>
          <button className="btn btn-primary" onClick={() => validar(m.COD, editando[m.COD] ?? m.PRECIO_SUGERIDO)}
            style={{ padding: '9px 16px', fontSize: 12.5, fontWeight: 700 }}>
            Validar
          </button>
        </div>
      ))}
    </div>
  );
}
